/* ============================================================
   Dathent — Settings screen
   Left tab-rail + main panel per tab. Port of design pack Settings.html.
   Scoped under .settings-page wrapper so pack CSS doesn't leak.
   ============================================================ */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { CONNECTABLE_PLATFORMS } from '../lib/platforms.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { notify } from '../lib/notifications.js';
import { useTimezoneSettings } from '../hooks/useTimezoneSettings.js';
import { TimezoneSettings } from '../components/TimezoneSettings.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import PostPayWelcome from './PostPayWelcome.jsx';
import PaymentMethodCard from '../components/billing/PaymentMethodCard.jsx';
import PaymentIssueNotice from '../components/PaymentIssueNotice.jsx';
import CreationAccessNotice from '../components/billing/CreationAccessNotice.jsx';
import FreePublishingAllowance from '../components/freePublishing/FreePublishingAllowance.jsx';
import { freePublishingState } from '../lib/freePublishing.js';
import { isDunningPaymentIssue } from '../lib/paymentIssue.js';
import { isManagedPaymentsSubscription } from '../lib/managedPayments.js';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import SocialConnectConfirm from '../components/billing/SocialConnectConfirm.jsx';
import PostingSubscriptionCard from '../components/billing/PostingSubscriptionCard.jsx';
import ScheduledPostingChangeCard from '../components/billing/ScheduledPostingChangeCard.jsx';
import ConnectedAccountsSheet from '../components/billing/ConnectedAccountsSheet.jsx';
import SocialAddonProvenanceLine from '../components/billing/SocialAddonProvenanceLine.jsx';
import { useGenerationStatus, OPERATION_KINDS } from '../state/GenerationStatusContext.jsx';
import { platformLabel } from '../lib/operations.js';
import { canRequestMetricsConsent, clearStaleOAuthReturnFlags, metricsConsentMissing, metricsConsentPlatformsFrom } from '../lib/metricsConsent.js';
import { useTikTokStatisticsAccounts } from '../components/metrics/TikTokStatisticsBanner.jsx';
import MetricsConsentDialog from '../components/metrics/MetricsConsentDialog.jsx';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { Toggle } from './content-plan/PublishingRulesTab.jsx';
import { PLAN_DEFS, PLAN_TEXT_KEYS, PLAN_ID_BY_KEY, NEW_PLAN_DEFS, NEW_PLAN_TEXT_KEYS, NEW_PLAN_ID_BY_KEY } from '../lib/plans.js';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { isPostingPlan } from '../lib/finishSetup.js';
import { CHATGPT_APP_URL, CLAUDE_CODE_COMMAND, CLAUDE_INSTALL_URL, CODEX_ADD_COMMAND, CODEX_LOGIN_COMMAND, MCP_ENDPOINT_URL } from '../lib/connectors.js';
import { track, trackOnce } from '../lib/funnel.js';
import { trackSubscribeOnce, pickInvoiceValue } from '../lib/gtag.js';
import { getFunnelSource } from '../lib/funnelSource.js';
import { socialCapacityFrom } from '../lib/socialCapacity.js';
import { buildPostingDisconnectCopy, buildPostingConnectSuccessCopy, countConnectedSocials, invoiceDisplayTitle } from '../lib/postingPlan.js';
import { resolveSocialAddonState, resolveSocialAddonPricing, wouldExceedIncluded, buildSocialAddonDisconnectCopy } from '../lib/socialAddon.js';
import { resolveScheduledDowngrade } from '../lib/planSwitchTrial.js';
import { useEmailVerificationActions } from '../lib/useEmailVerificationActions.js';
import { effectiveAccessState, canResumeSubscription, subscriptionResumeHasConverged, trialActivationHasConverged } from '../lib/billingState.js';

const PLAN_TIER_LEVEL = { pilot: 1, operator: 2, multi: 3, enterprise: 4 };

// accessState values under which a "Cancel subscription" action makes sense —
// i.e. a live Stripe subscription actually exists to cancel. Deliberately
// excludes 'lapsed' (nothing left to cancel — the org already lost writes) and
// 'new'/'free' (no subscription at all).
// TASK-331 phase 2 (Subscription Schedules): 'downgrade_scheduled' is still a
// live subscription on its CURRENT plan (only a future Stripe Subscription
// Schedule differs) — cancelling it is exactly as valid as cancelling a plain
// 'active' one, and cancel-subscription.ts itself releases any live schedule
// before cancelling (design brief item 4: "cancel wins").
const ACCESS_STATES_WITH_ACTIVE_SUBSCRIPTION = new Set(['trialing', 'active', 'grace', 'cancel_scheduled', 'downgrade_scheduled']);

// Resolves an existing subscriber's backend planKey to a display id, whether
// they're on a legacy plan (brand_pilot/marketing_operator/multi_brand/
// enterprise) or a NEW-grid plan (starter/growth/pro/agency — checked first,
// via NEW_PLAN_ID_BY_KEY). The two key namespaces are disjoint by
// construction (see lib/plans.js's collision note on the shared 'growth'
// string), so checking the new map unconditionally — not just when
// newGridActive — is safe: a legacy subscriber's planKey can never match it,
// and a new-grid subscriber (post-flip) is resolved correctly even if this
// component instance hasn't independently re-derived newGridActive.
function resolveCurrentPlanId(entitlements) {
  if (!entitlements) return null;
  // Backend `/api/billing/entitlements` returns `plan` (planKey from entitlement-plans).
  // Some legacy callers may pass `planKey`/`planId`.
  const planKey = entitlements.plan || entitlements.planKey;
  if (planKey === 'posting') return 'posting';
  if (planKey && NEW_PLAN_ID_BY_KEY[planKey]) return NEW_PLAN_ID_BY_KEY[planKey];
  if (planKey && PLAN_ID_BY_KEY[planKey]) return PLAN_ID_BY_KEY[planKey];
  if (entitlements.planId && PLAN_TIER_LEVEL[entitlements.planId]) return entitlements.planId;
  // Last-resort: match by human-readable name.
  const name = String(entitlements.planName || planKey || '').toLowerCase();
  if (name.includes('pilot')) return 'pilot';
  if (name.includes('operator')) return 'operator';
  if (name.includes('multi')) return 'multi';
  if (name.includes('enterprise')) return 'enterprise';
  return null;
}

// Appearance & language tab removed — Topbar already exposes ThemeToggle +
// LangSwitch globally on every screen, so a dedicated Settings tab for the
// same two controls was redundant. The AppearanceTab function below is
// kept (un-rendered) for now so an external deep-link like
// `?tab=appearance` doesn't crash — switchTab gracefully falls back to
// Profile when the id isn't in TABS. Safe to delete later if needed.
const TABS = [
  { id: 'profile', group: 'Account', label: 'Profile' },
  { id: 'security', group: 'Account', label: 'Security · 2FA' },
  { id: 'workspace', group: 'Workspace', label: 'Brand & workspace' },
  { id: 'accounts', group: 'Workspace', label: 'Connected social accounts' },
  { id: 'billing', group: 'Billing', label: 'Plan & billing' },
  { id: 'usage', group: 'Billing', label: 'Usage' },
  { id: 'connectors', group: 'Developer', label: 'API & Connectors' },
];

// i18n key suffixes for tab labels / group headers (rendered via t()).
// The raw English on TABS above stays as a stable, untranslated reference.
const TAB_LABEL_KEYS = {
  profile: 'tabProfile',
  security: 'tabSecurity',
  workspace: 'tabWorkspace',
  accounts: 'tabAccounts',
  billing: 'tabBilling',
  usage: 'tabUsage',
  connectors: 'tabConnectors',
};
const GROUP_LABEL_KEYS = {
  Account: 'groupAccount',
  Workspace: 'groupWorkspace',
  Billing: 'groupBilling',
  Developer: 'groupDeveloper',
};

function fmtMoney(amount, currency = 'USD') {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ---------------- Profile tab ---------------- */
function ProfileTab({ user }) {
  const { t } = useTranslation();
  const name = user?.name || user?.fullName || '';
  const email = user?.email || '';
  const { logout, reloadUser } = useAuth() || {};
  const deletionRequestedAt = user?.deletionRequestedAt || null;
  // TASK (2026-09-10): emailVerificationWallEnabled() went default OFF (owner
  // decision — e-mail confirmation must not block using the product), so an
  // unconfirmed address no longer holds the user at a full-screen wall. This
  // quiet card is the replacement nudge; the hook is shared with
  // VerifyEmailWall.jsx (lib/useEmailVerificationActions.js) so both get the
  // exact same resend/confirm request + state machine.
  const emailVerify = useEmailVerificationActions();

  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [dangerError, setDangerError] = useState(null);

  const scheduledFor = deletionRequestedAt
    ? new Date(new Date(deletionRequestedAt).getTime() + 30 * 24 * 60 * 60 * 1000)
        .toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const doDelete = async () => {
    if (confirmText.trim().toUpperCase() !== 'DELETE' || busy) return;
    setBusy(true);
    setDangerError(null);
    try {
      // Auto cancel-at-period-end any active subscription first, while the
      // session is still valid. Best-effort — harmless no-op if there's no
      // active plan (we swallow that error).
      try { await api.billing.cancelSubscription({ cancelAtPeriodEnd: true }); } catch (e) { /* no active sub */ }
      await api.auth.requestAccountDeletion();
      // Sessions are revoked server-side; sign out locally and leave.
      try { await logout?.(); } catch (e) { /* ignore */ }
      window.location.assign('/login?deleted=1');
    } catch (e) {
      setBusy(false);
      setDangerError(e?.message || t('settings.deleteScheduleError'));
    }
  };

  const cancelDeletion = async () => {
    if (busy) return;
    setBusy(true);
    setDangerError(null);
    try {
      await api.auth.cancelAccountDeletion();
      await reloadUser?.();
    } catch (e) {
      setDangerError(e?.message || t('settings.deleteCancelError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.profileTitle')}</h3></div>
        <div className="s-card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
            <div className="avatar">{(name || email || 'U').slice(0, 2).toUpperCase()}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{name || t('settings.unnamedAccount')}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>{email}</div>
            </div>
          </div>
          <div className="form-grid">
            <label className="form-row">
              <span className="form-l">{t('settings.fullName')}</span>
              <input className="ep-input" value={name} readOnly disabled />
            </label>
            <label className="form-row">
              <span className="form-l">{t('settings.email')}</span>
              <input className="ep-input" value={email} readOnly disabled />
            </label>
          </div>
          <p style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-subtle)' }}>
            {t('settings.profileEditingNotWired')}
          </p>
        </div>
      </section>

      {user?.emailVerified === false && (
        <section className="s-card">
          <div className="s-card-head"><h3>{t('auth.confirmYourEmail')}</h3></div>
          <div className="s-card-body">
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--text-subtle)' }}>
              {t('settings.emailVerifyBody')}
            </p>
            {emailVerify.sent && (
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--text-subtle)' }}>
                {t('auth.confirmResent', 'A fresh link is on its way — check your inbox (and spam).')}
              </p>
            )}
            {emailVerify.error && (
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#e5484d' }}>{emailVerify.error}</p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <button className="btn btn-ghost btn-sm" onClick={emailVerify.resend} disabled={emailVerify.sending} type="button">
                {emailVerify.sending ? t('auth.sending') : t('settings.emailVerifyResendCta')}
              </button>
              <button
                type="button"
                className="btn-ghost"
                style={{ background: 'none', border: 0, color: 'var(--text-subtle)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                onClick={emailVerify.refresh}
                disabled={emailVerify.checking}
              >
                {emailVerify.checking ? t('auth.checking', 'Checking…') : t('settings.emailVerifyConfirmedCta')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* GH-1134: re-open the cookie banner (CookieConsentBanner listens for data-cookie-settings). */}
      <section className="s-card">
        <div className="s-card-head"><h3>{t('cookieConsent.settingsTitle')}</h3></div>
        <div className="s-card-body">
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-subtle)' }}>
            {t('cookieConsent.settingsBody')}
          </p>
          <button type="button" className="btn btn-ghost btn-sm" data-cookie-settings="">
            {t('cookieConsent.settingsButton')}
          </button>
        </div>
      </section>

      <section className="s-card" style={{ borderColor: 'rgba(229,72,77,0.4)' }}>
        <div className="s-card-head"><h3 style={{ color: '#e5484d' }}>{t('settings.dangerZone')}</h3></div>
        <div className="s-card-body">
          {deletionRequestedAt ? (
            <>
              <p style={{ fontSize: 13, margin: '0 0 12px' }}>
                {t('settings.deletionScheduledPrefix')} <strong>{scheduledFor}</strong>. {t('settings.deletionScheduledSuffix')}
              </p>
              <button className="btn btn-violet btn-sm" onClick={cancelDeletion} disabled={busy} type="button">
                {busy ? t('settings.cancelling') : t('settings.cancelDeletion')}
              </button>
            </>
          ) : !showDelete ? (
            <>
              <p style={{ fontSize: 13, margin: '0 0 12px', color: 'var(--text-subtle)' }}>
                {t('settings.deleteAccountIntro')}
              </p>
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: '#e5484d', borderColor: 'rgba(229,72,77,0.4)' }}
                onClick={() => { setShowDelete(true); setDangerError(null); }}
                type="button"
              >
                {t('settings.deleteMyAccount')}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, margin: '0 0 10px' }}>
                {t('settings.deleteConfirmIntro')} <strong>DELETE</strong>.
              </p>
              <input
                className="ep-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{ marginBottom: 10, maxWidth: 220 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-sm"
                  style={{ background: '#e5484d', color: '#fff', opacity: confirmText.trim().toUpperCase() === 'DELETE' ? 1 : 0.5 }}
                  onClick={doDelete}
                  disabled={busy || confirmText.trim().toUpperCase() !== 'DELETE'}
                  type="button"
                >
                  {busy ? t('settings.scheduling') : t('settings.permanentlyDelete')}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowDelete(false); setConfirmText(''); }} disabled={busy} type="button">
                  {t('settings.keepMyAccount')}
                </button>
              </div>
            </>
          )}
          {dangerError && <p style={{ fontSize: 12.5, color: '#e5484d', marginTop: 10 }}>{dangerError}</p>}
        </div>
      </section>
    </>
  );
}

/* ---------------- Security tab ---------------- */
function SecurityTab() {
  const { t } = useTranslation();
  const [mfa, setMfa] = useState(null);
  const [loading, setLoading] = useState(true);
  // Password change / first-time set
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.auth.getMfaStatus()
      .then((res) => { if (!cancelled) setMfa(res?.data || res); })
      .catch(() => { if (!cancelled) setMfa(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const enabled = Boolean(mfa?.enrolled || mfa?.enabled);

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 8) { setPwMsg({ ok: false, text: t('settings.passwordTooShort') }); return; }
    if (newPw !== confirmPw) { setPwMsg({ ok: false, text: t('settings.passwordsDoNotMatch') }); return; }
    setPwBusy(true);
    try {
      await api.auth.changePassword({ currentPassword: curPw || undefined, newPassword: newPw });
      setPwMsg({ ok: true, text: t('settings.passwordUpdated') });
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwMsg({ ok: false, text: err?.body?.error?.message || err?.message || t('settings.passwordUpdateError') });
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <>
      <section className="s-card">
        <div className="s-card-head">
          <h3>{t('settings.twoFactorAuth')}</h3>
          {enabled && <span className="pill success"><span className="dot" />{t('settings.enabled')}</span>}
        </div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            {t('settings.twoFactorDesc')}
          </p>
          {loading ? (
            <div className="muted" style={{ fontSize: 13 }}>{t('settings.loading')}</div>
          ) : enabled ? (
            <p style={{ fontSize: 13 }}>{t('settings.twoFactorActive')}</p>
          ) : (
            <Link to="/2fa/setup" className="btn btn-violet btn-sm">{t('settings.enable2fa')}</Link>
          )}
        </div>
      </section>

      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.password')}</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            {t('settings.passwordDesc')}
          </p>
          <form onSubmit={submitPassword} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380 }}>
            <input className="ep-input" type="password" autoComplete="current-password"
                   placeholder={t('settings.currentPasswordPlaceholder')}
                   value={curPw} onChange={(e) => setCurPw(e.target.value)} />
            <input className="ep-input" type="password" autoComplete="new-password"
                   placeholder={t('settings.newPasswordPlaceholder')}
                   value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            <input className="ep-input" type="password" autoComplete="new-password"
                   placeholder={t('settings.confirmPasswordPlaceholder')}
                   value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
            <button type="submit" className="btn btn-violet btn-sm" disabled={pwBusy || !newPw} style={{ alignSelf: 'flex-start' }}>
              {pwBusy ? t('settings.saving') : t('settings.updatePassword')}
            </button>
            {pwMsg && (
              <p style={{ fontSize: 12.5, marginTop: 2, color: pwMsg.ok ? 'var(--success-text)' : '#e5484d' }}>
                {pwMsg.text}
              </p>
            )}
          </form>
        </div>
      </section>
    </>
  );
}

/* ---------------- Appearance tab ---------------- */
function AppearanceTab() {
  const { t, i18n } = useTranslation();
  return (
    <>
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.theme')}</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            {t('settings.themeDesc')}
          </p>
          <ThemeToggle />
        </div>
      </section>
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.language')}</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            {t('settings.currentLabel')} <strong>{i18n.language}</strong>. {t('settings.languageProgressNote')}
          </p>
          <LangSwitch />
        </div>
      </section>
    </>
  );
}

/* ---------------- Workspace tab ---------------- */
export function WorkspaceTab() {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const timezone = useTimezoneSettings();
  const { brand, brandStatus } = timezone;
  const loading = brandStatus === 'loading';
  const brandCtx = useBrand();
  const showBrandNameInCopy = !!brandCtx?.enabled && (brandCtx?.brands?.length || 0) > 1;

  // TASK-325: "Save post visuals to Media library" — a per-organization
  // setting, so a solo/no-org account never sees the row (server treats it
  // as always ON for that case; there is nothing to configure).
  const hasOrganization = !!user?.organizationId;
  const [mediaAutoSave, setMediaAutoSave] = useState(true);
  const [orgSettingsLoading, setOrgSettingsLoading] = useState(true);
  const [mediaAutoSaveSaving, setMediaAutoSaveSaving] = useState(false);
  // Org setting: "Generate an image for posts created by connected AI
  // agents" (default ON). Same org-settings object as mediaAutoSave above —
  // reuses that one GET rather than firing a second request.
  const [agentImage, setAgentImage] = useState(true);
  const [agentImageSaving, setAgentImageSaving] = useState(false);
  useEffect(() => {
    if (!hasOrganization) { setOrgSettingsLoading(false); return; }
    let cancelled = false;
    api.profile.getOrgSettings()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res;
        // Absent value reads as ON, matching the migration's DEFAULT 1 and
        // the server's own fail-open default.
        setMediaAutoSave(data?.mediaAutoSavePostVisuals !== false);
        setAgentImage(data?.generateImageForAgentDrafts !== false);
      })
      .catch(() => { if (!cancelled) { setMediaAutoSave(true); setAgentImage(true); } })
      .finally(() => { if (!cancelled) setOrgSettingsLoading(false); });
    return () => { cancelled = true; };
  }, [hasOrganization]);

  const handleToggleMediaAutoSave = async (next) => {
    const previous = mediaAutoSave;
    setMediaAutoSave(next);
    setMediaAutoSaveSaving(true);
    try {
      await api.profile.updateOrgSettings({ mediaAutoSavePostVisuals: next });
    } catch {
      setMediaAutoSave(previous);
    } finally {
      setMediaAutoSaveSaving(false);
    }
  };

  const handleToggleAgentImage = async (next) => {
    const previous = agentImage;
    setAgentImage(next);
    setAgentImageSaving(true);
    try {
      await api.profile.updateOrgSettings({ generateImageForAgentDrafts: next });
    } catch {
      setAgentImage(previous);
    } finally {
      setAgentImageSaving(false);
    }
  };

  return (
    <>
      <section className="s-card">
        <div className="s-card-head">
          <h3>{t('settings.brandWorkspace')}</h3>
          <div className="sp" />
          <Link to="/app/plan" className="btn btn-ghost btn-sm">{t('settings.editInContentPlan')}</Link>
        </div>
        <div className="s-card-body">
          {loading ? <div className="muted">{t('settings.loadingBrandProfile')}</div>
            : brandStatus === 'error' ? null
            : !brand ? <p className="muted">{t('settings.noBrandProfile')}</p>
            : (
              <dl className="info-list">
                {/*
                  Field names corrected to match what the brand-profile API
                  actually returns (BrandProfile type in shared-types).
                  Earlier version read `brand.brandName` / `brand.name` /
                  `brand.tone` / `brand.tagline` / `brand.industry` — none
                  of those exist in the response shape, so every row except
                  Audience always rendered "—" regardless of stored data.
                  The DB columns ARE populated (business_name, target_
                  audience, tone_of_voice, etc.); the display layer was
                  just looking at the wrong keys.
                  tagline + industry rows removed entirely because no such
                  fields exist anywhere in the schema. customToneDescription
                  surfaces here as "Voice" — the new free-form field added
                  in EditStrategy → "How your brand sounds".
                */}
                <div><dt>{t('settings.brandName')}</dt><dd>{brand.businessName || '—'}</dd></div>
                <div><dt>{t('settings.whatYouDo')}</dt><dd>{brand.businessDescription || '—'}</dd></div>
                <div><dt>{t('settings.audience')}</dt><dd>{brand.targetAudience || '—'}</dd></div>
                <div><dt>{t('settings.tone')}</dt><dd>{brand.toneOfVoice || '—'}</dd></div>
                {brand.customToneDescription && (
                  <div><dt>{t('settings.voice')}</dt><dd>{brand.customToneDescription}</dd></div>
                )}
              </dl>
            )}
        </div>
      </section>

      <section className="s-card">
        <div className="s-card-head"><h3>
          {t('settings.timezoneTitle')}
          {showBrandNameInCopy && brand?.businessName ? ` (${t('settings.timezoneForBrand', { brand: brand.businessName })})` : ''}
        </h3></div>
        <div className="s-card-body"><TimezoneSettings controller={timezone} /></div>
      </section>
      {hasOrganization && (
        <section className="s-card">
          <div className="s-card-head"><h3>{t('settings.mediaLibrarySection')}</h3></div>
          <div className="s-card-body">
            <div className="ds-setting-row" style={{ borderTop: 0, paddingTop: 0 }}>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{t('settings.mediaAutoSaveLabel')}</div>
                <div className="caption muted" style={{ fontSize: 12 }}>{t('settings.mediaAutoSaveDescription')}</div>
              </div>
              <Toggle
                checked={!!mediaAutoSave}
                onChange={handleToggleMediaAutoSave}
                disabled={orgSettingsLoading || mediaAutoSaveSaving}
                label={t('settings.mediaAutoSaveLabel')}
              />
            </div>
          </div>
        </section>
      )}

      {hasOrganization && (
        <section className="s-card">
          <div className="s-card-head"><h3>{t('settings.agentImageSection')}</h3></div>
          <div className="s-card-body">
            <div className="ds-setting-row" style={{ borderTop: 0, paddingTop: 0 }}>
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{t('settings.agentImageLabel')}</div>
                <div className="caption muted" style={{ fontSize: 12 }}>{t('settings.agentImageDescription')}</div>
              </div>
              <Toggle
                checked={!!agentImage}
                onChange={handleToggleAgentImage}
                disabled={orgSettingsLoading || agentImageSaving}
                label={t('settings.agentImageLabel')}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
}

/* ---------------- Accounts tab ---------------- */
// Messages for OAuth callback results — keys match auth-worker error codes.
// Maps auth-worker error codes -> i18n key suffixes. title/body resolved via
// t() at render; cta.href is a real URL and stays untouched.
const OAUTH_ERROR_MESSAGES = {
  no_facebook_pages: {
    titleKey: 'oauthNoFacebookPagesTitle',
    bodyKey: 'oauthNoFacebookPagesBody',
    cta: { labelKey: 'oauthNoFacebookPagesCta', href: 'https://www.facebook.com/pages/create' },
  },
  no_instagram_business_accounts: {
    titleKey: 'oauthNoInstagramBusinessTitle',
    bodyKey: 'oauthNoInstagramBusinessBody',
    cta: { labelKey: 'oauthNoInstagramBusinessCta', href: 'https://business.facebook.com/latest/settings/profiles' },
  },
  missing_code_or_state: { titleKey: 'oauthMissingParamsTitle', bodyKey: 'oauthMissingParamsBody' },
  invalid_or_expired_state: { titleKey: 'oauthExpiredStateTitle', bodyKey: 'oauthExpiredStateBody' },
  meta_oauth_failed: { titleKey: 'oauthMetaFailedTitle', bodyKey: 'oauthMetaFailedBody' },
  instagram_oauth_failed: { titleKey: 'oauthInstagramFailedTitle', bodyKey: 'oauthInstagramFailedBody' },
  threads_oauth_failed: { titleKey: 'oauthThreadsFailedTitle', bodyKey: 'oauthThreadsFailedBody' },
  tiktok_oauth_failed: { titleKey: 'oauthTiktokFailedTitle', bodyKey: 'oauthTiktokFailedBody' },
  x_oauth_failed: { titleKey: 'oauthXFailedTitle', bodyKey: 'oauthXFailedBody' },
  // Direct Instagram-Login path: no business profile came back. The most
  // common cause is a personal (non-Professional) Instagram account.
  instagram_missing_user_id: {
    titleKey: 'oauthInstagramProfessionalTitle',
    bodyKey: 'oauthInstagramProfessionalBody',
    cta: { labelKey: 'oauthInstagramProfessionalCta', href: 'https://help.instagram.com/502981923235522' },
  },
  instagram_token_exchange_failed: {
    titleKey: 'oauthInstagramTokenFailedTitle',
    bodyKey: 'oauthInstagramTokenFailedBody',
  },
  instagram_user_info_failed: {
    titleKey: 'oauthInstagramUserInfoTitle',
    bodyKey: 'oauthInstagramUserInfoBody',
    cta: { labelKey: 'oauthInstagramProfessionalCta', href: 'https://help.instagram.com/502981923235522' },
  },
  threads_missing_user_id: {
    titleKey: 'oauthThreadsProfileTitle',
    bodyKey: 'oauthThreadsProfileBody',
  },
  threads_token_exchange_failed: {
    titleKey: 'oauthThreadsTokenFailedTitle',
    bodyKey: 'oauthThreadsTokenFailedBody',
  },
  threads_user_info_failed: {
    titleKey: 'oauthThreadsUserInfoTitle',
    bodyKey: 'oauthThreadsUserInfoBody',
  },
  // New enforcement (all platforms): the backend redirects here when the
  // plan's social_accounts_total cap is already hit, before even attempting
  // the OAuth handshake. TASK-297 added the `cta` below (an internal route,
  // unlike every other entry's external `href`) so this notice — reached only
  // when the pre-connect hint in the "Connect a new account" section below
  // somehow didn't stop the attempt first (e.g. a stale page, a second tab) —
  // still gives a way out instead of leaving the user stuck.
  social_accounts_limit_reached: {
    titleKey: 'oauthSocialLimitTitle',
    bodyKey: 'oauthSocialLimitBody',
    cta: { labelKey: 'changePlan', to: '/app/plans' },
  },
};

function platformLabelOf(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'x') return 'X';
  if (v === 'tiktok') return 'TikTok';
  if (v === 'linkedin') return 'LinkedIn';
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Account';
}

// Languages this app generates posts in. Stays in sync with auth-worker
// handlers/social-accounts.ts SUPPORTED_PUBLISH_LANGUAGES.
const PUBLISH_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'cs', label: 'Čeština' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'sv', label: 'Svenska' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'lv', label: 'Latviešu' },
];

// Brand profiles created before the language-normalization fix may
// have raw text in `language` (e.g. "Ukrainian / English / Lithuanian
// for test"). Pull out the first recognizable language so the chip
// shows "Default (Ukrainian)" instead of "Default (UKRAINIAN / ENGLISH
// / LITHUANIAN FOR TEST)".
function normalizeOrgLanguage(raw) {
  if (!raw) return 'en';
  const lower = String(raw).toLowerCase().trim();
  const direct = PUBLISH_LANGUAGES.find((l) => l.code === lower);
  if (direct) return direct.code;
  const patterns = [
    [/\b(english|англ|en)\b/, 'en'],
    [/\b(ukrainian|українськ|укр|uk|ua)\b/, 'uk'],
    [/\b(russian|русск|ru)\b/, 'ru'],
    [/\b(german|deutsch|німецьк|de)\b/, 'de'],
    [/\b(french|français|francais|франц|fr)\b/, 'fr'],
    [/\b(spanish|español|espanol|испан|es)\b/, 'es'],
    [/\b(portuguese|portugu|порту|pt)\b/, 'pt'],
    [/\b(italian|italiano|італій|итал|it)\b/, 'it'],
    [/\b(polish|polski|польськ|польск|pl)\b/, 'pl'],
    [/\b(lithuanian|lietuv|литов|lt)\b/, 'lt'],
  ];
  for (const [re, code] of patterns) {
    if (re.test(lower)) return code;
  }
  return 'en';
}

// Per-account language chip. Two states:
//  - Entitled (Growth+): clickable chip wrapping a hidden native <select>.
//    Shows "Language: English ▾" — explicit so users don't wonder what the
//    chip means. Selecting a value writes through to publish_language.
//  - Locked (free/starter): dashed chip with lock icon, hover tooltip
//    explains gate, click routes to billing.
//
// When the account has no override (value === null), we show the org-wide
// default explicitly: "Language: Default (English)" — so the user sees
// what language posts will ship in, not just that "Default" is set.
function AccountLanguageChip({ value, orgDefault, canOverride, isPosting, saving, onChange, onUpgrade }) {
  const { t } = useTranslation();
  const code = (value || '').toLowerCase() || null;
  // Always normalize the org-wide default to a known ISO code — handles
  // legacy raw-text languages from pre-fix brand profiles.
  const orgCode = normalizeOrgLanguage(orgDefault);
  const orgLabel = PUBLISH_LANGUAGES.find(l => l.code === orgCode)?.label || orgCode.toUpperCase();
  const fullLabel = code
    ? (PUBLISH_LANGUAGES.find(l => l.code === code)?.label || code)
    : t('settings.languageDefaultWithLabel', { label: orgLabel });
  // TASK-315: Scheduler never offers the per-account override, and (unlike
  // the free/starter locked chip below) must not carry a click-through to
  // billing either — this plan does not upsell per-account language, it just
  // states the fact as a quiet pill.
  if (!canOverride && isPosting) {
    return (
      <span className="account-lang-chip is-static" title={t('settings.languagePostingTagTitle')}>
        <I.globe size={12} />
        <span className="account-lang-value">{t('settings.languagePostingTag')}</span>
      </span>
    );
  }
  if (!canOverride) {
    return (
      <button
        type="button"
        className="account-lang-chip is-locked"
        onClick={onUpgrade}
        title={t('settings.languageLockedTitle', { label: orgLabel })}
        aria-label={t('settings.languageLockedAria', { label: fullLabel })}
      >
        <I.globe size={12} />
        <span className="account-lang-label">{t('settings.languageLabel')}</span>
        <span className="account-lang-value">{fullLabel}</span>
        {/* Inline lock SVG — pack-icons doesn't ship one. */}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </button>
    );
  }
  return (
    <label
      className="account-lang-chip is-editable"
      style={{ opacity: saving ? 0.6 : 1 }}
      title={code ? t('settings.languagePostsTo', { label: fullLabel }) : t('settings.languageInheritsTitle', { label: orgLabel })}
    >
      <I.globe size={12} />
      <span className="account-lang-label">{t('settings.languageLabel')}</span>
      <span className="account-lang-value">{fullLabel}</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ opacity: 0.7 }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
      <select
        value={code || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={saving}
        style={{
          position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer',
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
        }}
        aria-label={t('settings.publishLanguage')}
      >
        <option value="">{t('settings.languageDefaultWithLabel', { label: orgLabel })}</option>
        {PUBLISH_LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </label>
  );
}

function AccountsTab() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dash = useDashboardData() || {};
  // Per-account language override = Growth plan or above.
  // Trigger entitlements fetch on mount — DashboardDataProvider only
  // auto-refreshes on the dashboard route, so without this nudge the
  // Settings page sees entitlements=null and falls into the locked
  // branch even for paying customers.
  useEffect(() => {
    if (typeof dash.ensureDashboardData === 'function') {
      dash.ensureDashboardData().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canOverrideLanguage = Boolean(dash.entitlements?.features?.per_account_language_override);
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [metricsConsentPlatforms, setMetricsConsentPlatforms] = useState(() => new Set());
  const [metricsGrantContext, setMetricsGrantContext] = useState(null);
  const [authReconnectContext, setAuthReconnectContext] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  // TASK-297: the TRUE (non-brand-scoped) social_accounts_total snapshot from
  // GET /social/accounts (auth-worker's getSocialAccountCapacity) — see
  // lib/socialCapacity.js for why this, and not accounts.length or
  // entitlements.usage, is the accurate source.
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [socialConnectPlatform, setSocialConnectPlatform] = useState(null);
  const [metricsConsentAccount, setMetricsConsentAccount] = useState(null);
  const [metricsConsentStarting, setMetricsConsentStarting] = useState(false);
  const [languageSavingId, setLanguageSavingId] = useState(null);
  const [notice, setNotice] = useState(null); // { kind: 'success'|'error', title, body, cta? }
  const { operationsArr, startOperation, completeOperation, failOperation, updateOperation } = useGenerationStatus();

  // Complete any pending social_connect ops once their platform's account
  // shows up in `accounts`. This is the "user came back from OAuth" hook —
  // the op was started before the redirect, persisted in sessionStorage,
  // and now that we see the connected account we mark it succeeded.
  useEffect(() => {
    if (!accounts.length) return;
    const connectedPlatforms = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()));
    operationsArr.forEach((op) => {
      if (op.kind !== OPERATION_KINDS.SOCIAL_CONNECT) return;
      if (op.status === 'succeeded' || op.status === 'failed' || op.status === 'cancelled') return;
      const targetPlatform = String(op.sourceRef?.id || '').toLowerCase();
      if (!targetPlatform) return;
      if (connectedPlatforms.has(targetPlatform)) {
        completeOperation(op.id, { status: 'succeeded' });
      }
    });
  }, [accounts, operationsArr, completeOperation]);

  const handleLanguageChange = async (accountId, language) => {
    if (!canOverrideLanguage) {
      navigate('/app/settings?tab=billing');
      return;
    }
    setLanguageSavingId(accountId);
    setNotice(null);
    // Optimistic update so the chip flips immediately.
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, publishLanguage: language || null } : a));
    try {
      await api.socialAccounts.updateLanguage(accountId, language || null);
      // Invalidate the dashboard-level cache so /app/create (and other
      // screens that read socialAccounts from useDashboardData) see the
      // new publishLanguage on the next render — without a full reload.
      // Fire-and-forget; the provider soft-fails on its own.
      dash.invalidateResource?.('socialAccounts');
    } catch (err) {
      const code = err?.body?.error?.code || err?.data?.error?.code;
      if (code === 'ENTITLEMENT_REQUIRED') {
        navigate('/app/settings?tab=billing');
        return;
      }
      setNotice({ kind: 'error', title: t('settings.languageUpdateErrorTitle'), body: errMessage(err) });
      refresh().catch(() => {});
    } finally {
      setLanguageSavingId(null);
    }
  };

  const refresh = () => {
    setLoading(true);
    return api.socialAccounts.list()
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        const nextAccounts = payload?.accounts || [];
        setAccounts(nextAccounts);
        setMetricsConsentPlatforms(metricsConsentPlatformsFrom(payload));
        // Org-wide default falls back to 'en' on the backend when null in D1
        // but here we keep null so the chip can show "Default (org default)"
        // unambiguously even if the user hasn't picked one yet.
        setDefaultLanguage(payload?.defaultPublishLanguage || null);
        setCapacity(payload?.capacity || null);
        // TASK-316: the posting OAuth-return success banner below needs the
        // freshly-fetched, org-wide accounts list (not the batch-sized
        // `accounts` URL param) to state the CURRENT total after connect.
        return nextAccounts;
      })
      .catch(() => { setAccounts([]); setMetricsConsentPlatforms(new Set()); return []; })
      .finally(() => setLoading(false));
  };

  // Parse OAuth result params on mount + on subsequent navigations from
  // /settings/integrations. Then strip the params from the URL so they don't
  // re-fire on refresh.
  useEffect(() => {
    const refreshed = refresh();
    const error = searchParams.get('error');
    const connected = searchParams.get('connected');
    const platform = searchParams.get('platform');
    const metricsIssue = searchParams.get('metrics');
    const metricsAccountId = searchParams.get('account');
    const reconnectIssue = searchParams.get('reconnect');
    const accountsCount = searchParams.get('accounts');
    // Meta multi-account connects can PARTIALLY succeed: some accounts land,
    // the rest are skipped because the plan's connected-accounts cap was hit
    // mid-batch. The backend signals that with limitReached=1 alongside the
    // normal connected/accounts success params.
    const limitReached = searchParams.get('limitReached');
    // auth-worker's overage guard (parallel work): an unauthorized
    // /oauth/<platform>/start attempt (no overage_ack yet) 302s here with
    // ?overage=<platform> instead of the usual 402 — reopen the SAME
    // <SocialConnectConfirm> sheet the user would have seen clicking
    // "Connect" from this tab, so a stale/bookmarked raw connect link still
    // lands on the price/billed screen. Gate mirrors handleConnect's own
    // `dash.entitlements?.plan === 'posting' || wouldExceedIncluded(social)`
    // below — the guard bounces here for BOTH billed shapes (the add-on
    // overage AND the Scheduler's own per-account billing), not just the
    // add-on's 'priced' mode. If neither holds any more — plan changed,
    // already within the included count — just show this tab as normal;
    // nothing to confirm.
    const overagePlatform = searchParams.get('overage');
    if (overagePlatform) {
      // Review round 2 (Opus, 2026-09-08): open the sheet UNCONDITIONALLY.
      // auth-worker is the authority on whether an acknowledgement is needed
      // — it also bounces here on its fail-closed branches (billing read
      // failure, null org), where this tab's own `plan === 'posting' ||
      // wouldExceedIncluded(...)` predicate would be false and the user would
      // land on an unchanged Accounts tab with no way forward. The sheet
      // fetches its own quote, renders sensibly in every state (incl. its
      // error branch), and EVERY Continue now sends overage_ack=1, so one
      // click always clears the guard. No ensureDashboardData() dependency
      // (a failing dashboard fetch must not be able to hide the sheet).
      setSocialConnectPlatform(String(overagePlatform).toLowerCase());
    }
    if (error === 'metrics_app_unavailable') {
      setNotice({ kind: 'info', title: t('socialMetrics.metricsUnavailableTitle'), body: t('socialMetrics.metricsUnavailable') });
    } else if (error === 'metrics_scope_not_granted' || error === 'metrics_wrong_account') {
      setMetricsGrantContext(platform ? { platform: String(platform).toLowerCase(), accountId: null } : null);
      setNotice({ kind: 'error', title: t('socialMetrics.permission'), body: t(error === 'metrics_wrong_account' ? 'socialMetrics.metricsWrongAccount' : 'socialMetrics.metricsUpgradeDenied') });
    } else if (metricsIssue === 'permission' && platform && metricsAccountId) {
      // Arrived from a concrete persisted-metrics permission failure for this
      // target. Server-reported provider capability still gates the action.
      setMetricsGrantContext({ platform: String(platform).toLowerCase(), accountId: metricsAccountId });
      setNotice({ kind: 'info', title: t('socialMetrics.permission'), body: t('socialMetrics.metricsUpgradeDenied') });
    } else if (reconnectIssue === 'authentication' && platform && metricsAccountId) {
      setAuthReconnectContext({ platform: String(platform).toLowerCase(), accountId: metricsAccountId });
    } else if (error) {
      const raw = OAUTH_ERROR_MESSAGES[error];
      const cfg = raw
        ? {
            title: t(`settings.${raw.titleKey}`),
            body: t(`settings.${raw.bodyKey}`),
            ...(raw.cta ? { cta: { label: t(`settings.${raw.cta.labelKey}`), href: raw.cta.href, to: raw.cta.to } } : {}),
          }
        : {
            title: t('settings.oauthGenericTitle', { platform: platformLabelOf(platform) }),
            body: t('settings.oauthGenericBody', { code: error }),
          };
      setNotice({ kind: 'error', ...cfg });
    } else if (connected && searchParams.get('keptBrand') === '1') {
      // The OAuth round-trip succeeded, but the account is still CONNECTED on
      // another brand, so the upsert deliberately kept it there (a live
      // account can't be stolen by another brand's connect) and it will not
      // appear in this brand's list. The old plain-success toast here read as
      // "connected" over an empty list — owner repro 2026-08-11.
      setNotice({
        kind: 'error',
        title: t('settings.oauthKeptBrandTitle', {
          platform: platformLabelOf(platform),
          defaultValue: '{{platform}} account is already linked to another brand',
        }),
        body: t('settings.oauthKeptBrandBody', {
          defaultValue: 'This account stays with the brand it is connected to. To move it here, switch to that brand, disconnect the account there, then connect it again from this brand.',
        }),
      });
    } else if (connected && limitReached === '1') {
      // Partial success: honest "connected N, skipped the rest" instead of the
      // plain success line. Body reuses the same plan-limit copy as the
      // full-block social_accounts_limit_reached error above.
      const n = Number(accountsCount) || 0;
      setNotice({
        kind: 'success',
        title: n > 0
          ? t('settings.oauthPartialLimitTitle', { count: n, defaultValue: 'Connected {{count}}, but some were skipped' })
          : t('settings.oauthPartialLimitTitleGeneric', 'Connected, but some accounts were skipped'),
        body: t('settings.oauthSocialLimitBody'),
      });
    } else if (connected) {
      // TASK-316 (ADDENDUM): the OAuth redirect round-trip is a full page
      // reload — entitlements has not necessarily loaded yet on this exact
      // tick (dash.entitlements can still be null here), so the posting
      // check waits on the SAME ensureDashboardData() promise this
      // component's other mount effect already triggers (deduped — no extra
      // fetch) rather than trusting the possibly-stale closure value. "1 account now available
      // for publishing" reads as wrong once a 2nd+ account is connected, so
      // Scheduler gets the current-total copy instead of the generic one.
      const title = t('settings.platformConnected', { platform: platformLabelOf(platform) });
      // fix #3 (judge review, commit 145b8f4): ensureDashboardData() returns
      // `{ ...stateRef.current, ...resolved }` — the freshly-resolved values
      // merged over the pre-fetch snapshot — precisely so `dashState` here
      // carries the just-fetched entitlements even though React has not yet
      // committed the corresponding setState() calls on this tick. Do not
      // replace this with `dash.entitlements` (the closure value): that would
      // read the STALE pre-fetch snapshot (often null right after an OAuth
      // redirect reload) and misroute every plan's success copy.
      Promise.all([refreshed, dash.ensureDashboardData()]).then(([freshAccounts, dashState]) => {
        if (dashState?.entitlements?.plan === 'posting') {
          const copy = buildPostingConnectSuccessCopy({ count: countConnectedSocials(freshAccounts), t });
          setNotice({ kind: 'success', title, body: copy.message });
          return;
        }
        setNotice({
          kind: 'success',
          title,
          body: accountsCount
            ? t('settings.accountsAvailableForPublishing', { count: Number(accountsCount) })
            : t('settings.accountReadyForPublishing'),
        });
      });
    }
    if (error || connected || overagePlatform || metricsIssue || reconnectIssue) {
      const next = new URLSearchParams(searchParams);
      // `overage` is stripped unconditionally (whether or not the sheet ends
      // up opening above) so a refresh never re-triggers it.
      ['error', 'connected', 'platform', 'account', 'metrics', 'reconnect', 'accounts', 'pages', 'instagramBusinessAccounts', 'provider', 'assistant', 'continue', 'limitReached', 'keptBrand', 'overage'].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep link to ONE platform's card (`?focus=<platform>`) — e.g. the
  // ChatGPT app's "Manage TikTok" link now appends `&focus=tiktok` instead
  // of dropping the user on the full, undifferentiated Accounts list.
  // Deliberately a SEPARATE effect from the OAuth-return handling above
  // (left exactly as-is): this one waits for `loading` to clear so the
  // account rows actually exist in the DOM before it looks for one, and
  // never touches the overage/connected/error params. Runs at most once
  // per mount (focusHandledRef), same one-shot idiom as the params above.
  const focusHandledRef = useRef(false);
  useEffect(() => {
    if (focusHandledRef.current) return;
    if (loading) return; // wait for the accounts list to actually render
    const focusPlatform = searchParams.get('focus');
    if (!focusPlatform) return;
    focusHandledRef.current = true;
    const platform = String(focusPlatform).toLowerCase();
    let prefersReducedMotion = false;
    try { prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { /* ignore */ }
    // First choice: the platform's connected card. Fallback: its "Connect
    // <platform>" control in the grid below. Neither present -> no-op.
    const target = document.querySelector(`.account-row[data-platform="${platform}"]`)
      || document.querySelector(`.connect-btn[data-platform="${platform}"]`);
    if (target) {
      try {
        target.scrollIntoView({ block: 'center', behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      } catch (_) { /* noop — still strip the param below */ }
      target.classList.add('is-focused');
      window.setTimeout(() => target.classList.remove('is-focused'), 2500);
    }
    // One-shot param, same idiom as the OAuth-return params above.
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const connectedPlatforms = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()));

  const socialCapacity = useMemo(
    () => socialCapacityFrom({ capacity, entitlements: dash.entitlements, accounts }),
    [capacity, dash.entitlements, accounts]
  );
  // TASK-329 round 2 fix (blocker 1): resolved through resolveSocialAddonState
  // — the ONE gate — instead of reading `.billable` directly, so this always
  // agrees with the Billing tab's own socialAddonPricing (both derive from
  // the same three-mode resolver; a raw `billable` check could pass while
  // unitAmount/currency were still null, which must NOT count as priced).
  const social = dash.entitlements?.socialAccounts || null;
  const socialAddonState = resolveSocialAddonState(social);
  // TASK-329 (owner decision, item 6): a billable org never hits the hard
  // cap block — it gets the SocialConnectConfirm dialog instead (see
  // handleConnect below). Every other org (free/no-card/lapsed/no-price-yet)
  // keeps today's behaviour unchanged.
  const capacityBlocked = socialCapacity.reached && !(socialAddonState.mode === 'priced');

  // One impression per time the hint actually becomes visible (not deduped
  // forever — a plan change that frees a slot and later re-fills it should
  // surface the hint again). Round 2 fix (MINOR): gated on `capacityBlocked`
  // itself (the hint banner's own render condition below), not the broader
  // `socialCapacity.reached` — a billable/priced org can be "at capacity"
  // without ever seeing this banner (item 6 above routes it through the
  // confirm dialog instead), so the old condition fired the analytics event
  // for an impression that never actually rendered.
  useEffect(() => {
    if (!capacityBlocked) return;
    try { track('social_limit_hint_shown', { used: socialCapacity.used, limit: socialCapacity.limit }); } catch (_) { /* fail-silent */ }
  }, [capacityBlocked, socialCapacity.used, socialCapacity.limit]);

  const startConnect = async (platform, meta = {}) => {
    setConnecting(platform);
    setNotice(null);
    // A connect started from Settings must return to Settings (see
    // clearStaleOAuthReturnFlags for why stale OAuth-return flags must go).
    clearStaleOAuthReturnFlags();
    // Telegram returns a bot-setup link instead of redirecting the tab. A
    // window.open() that runs AFTER the connect await is treated as
    // non-user-initiated and silently blocked — so open a blank tab NOW,
    // synchronously inside the click, and point it at the link once it
    // resolves. Non-telegram platforms keep tgWindow = null.
    let tgWindow = null;
    if (platform === 'telegram') {
      tgWindow = window.open('about:blank', '_blank');
      if (tgWindow) { try { tgWindow.opener = null; } catch (_) { /* noop */ } }
    }
    // Register the pending connection in the global status plate. Persists
    // via sessionStorage so it survives the OAuth redirect → callback →
    // settings reload roundtrip. The pending-account effect below
    // completes it once the new account appears in `accounts`.
    // An existing account appearing in the list cannot prove that new metrics
    // scopes were granted. Do not use the normal connect-success operation.
    const opId = meta?.metrics === true ? null : startOperation({
      kind: OPERATION_KINDS.SOCIAL_CONNECT,
      label: t('settings.connectingPlatform', { platform: platformLabel(platform) }),
      status: 'pending',
      total: null,
      sourceRef: { type: 'socialAccount', id: platform },
      deeplink: '/app/settings?tab=accounts',
      expectedDurationMs: 60_000,
    });
    try {
      // Every platform (Instagram included) uses its PRIMARY connect flow.
      // Instagram's primary flow is the direct Instagram Login API
      // (instagram.com) — that is exactly what the in-progress App Review
      // (app 2197) documents, so the live app must match it for the review to
      // pass. Users whose IG is linked to a Facebook Page use the clearly
      // labelled "Connect via Facebook Page" secondary below.
      const result = await api.socialAccounts.connect(platform, {
        returnPath: '/app/settings?tab=accounts',
        // TASK-329 round 4 fix (guard contract, 2nd review pass):
        // <SocialConnectConfirm>'s onContinue now sends overageAck: true on
        // EVERY Continue click (that sheet is always the price screen, no
        // matter which branch it rendered) — every plain/direct connect
        // below the included count still leaves `meta` at its default {},
        // so this stays a pass-through rather than hardcoding true here.
        overageAck: meta?.overageAck === true,
        // handleReconnect (below) passes reconnect: true for a re-auth of an
        // account the org already has connected — the auth-worker guard
        // exempts the ack requirement for reconnect=1 in exactly that case.
        reconnect: meta?.reconnect === true,
        metrics: meta?.metrics === true,
        accountId: meta?.metrics === true ? meta?.accountId : undefined,
      });
      // Telegram's createTelegramLink() returns { linkUrl }; OAuth platforms
      // already navigated the tab and return no link here.
      const tgUrl = result?.linkUrl || result?.link?.url || result?.deepLink || null;
      if (tgUrl) {
        // Carry the bot link on the Activity operation so its "Open" re-opens
        // Telegram (the connect stays pending until the channel is linked, and
        // the user may have closed the original tab).
        updateOperation(opId, { externalUrl: tgUrl });
        if (tgWindow) tgWindow.location.href = tgUrl;
        else window.open(tgUrl, '_blank', 'noopener');
      } else if (tgWindow) {
        tgWindow.close();
      }
      // Redirect happens via window.location.assign for OAuth platforms
      return result;
    } catch (err) {
      if (tgWindow) tgWindow.close();
      setNotice({ kind: 'error', title: t('settings.connectionFailedToStart'), body: errMessage(err) });
      if (opId) failOperation(opId, errMessage(err) || t('settings.connectionFailed'));
    } finally {
      setConnecting(null);
    }
  };

  // TASK-329 round 2 fix (blocker 1/5): `social`/`socialAddonState` are
  // already resolved above (AccountsTab's own state, shared with
  // capacityBlocked) — wouldExceedIncluded(social) mirrors auth-worker's
  // own allow-over-limit predicate so this dialog opens in exactly the
  // cases the backend would otherwise 402 on (and now allows, billed, when
  // canBillSocialOverage). It already resolves through the SAME 'priced'
  // gate internally, so no separate `social?.billable &&` check is needed
  // here any more (a duplicate that could drift from it).
  const handleConnect = (platform) => {
    if (dash.entitlements?.plan === 'posting' || wouldExceedIncluded(social)) {
      setSocialConnectPlatform(platform);
      return;
    }
    startConnect(platform);
  };

  const handleDisconnect = async (id) => {
    const account = accounts.find((a) => a.id === id);
    const label = account?.displayName || account?.externalAccountId || platformLabelOf(account?.platform);
    // TASK-329: an overage disconnect (this account is one of the billed
    // `extra`) shows the bill-drop copy instead of the plain confirm — the
    // Scheduler branch (unchanged) always shows its own quantity-based copy.
    const isOverageDisconnect = socialAddonState.mode === 'priced' && socialAddonState.extra > 0;
    const confirmCopy = dash.entitlements?.plan === 'posting'
      ? buildPostingDisconnectCopy({ label, currentQuantity: countConnectedSocials(accounts), t }).message
      : isOverageDisconnect
        ? buildSocialAddonDisconnectCopy({
            label,
            connected: socialAddonState.connected,
            included: socialAddonState.included,
            unitAmount: socialAddonState.unitAmount,
            currency: socialAddonState.currency,
            locale: i18n.language || 'en',
            t,
          }).message
        : t('settings.disconnectConfirm', { label });
    if (dash.entitlements?.plan === 'posting') {
      try { track('posting_disconnect_confirm_shown', { accountId: id }); } catch (_) { /* fail-silent */ }
    } else if (isOverageDisconnect) {
      try { track('social_addon_disconnect_confirm_shown', { accountId: id }); } catch (_) { /* fail-silent */ }
    }
    if (!window.confirm(confirmCopy)) return;
    setNotice(null);
    try {
      await api.socialAccounts.disconnect(id);
      // Drop the dashboard-level socialAccounts cache too. New Post / Create read
      // accounts from useDashboardData (separate from this screen's local list),
      // so without this they keep offering the just-disconnected channel for up
      // to the 2-min TTL. Mirrors what updateLanguage already does.
      dash.invalidateResource?.('socialAccounts');
      await refresh();
      setNotice({ kind: 'success', title: t('settings.accountDisconnected'), body: t('settings.accountDisconnectedBody', { label }) });
    } catch (err) {
      setNotice({ kind: 'error', title: t('settings.disconnectFailed'), body: errMessage(err) });
    }
  };

  const handleReconnect = async (account) => {
    const platform = String(account.platform || '').toLowerCase();
    // TASK-329 round 3 fix (BLOCKER): this button (below) only ever renders
    // for `needs_reconnect` | `expired` | `failed` rows — every one of
    // those is an account the org ALREADY has connected and ALREADY counts
    // toward its total, so re-authorizing it does not add a billed social
    // and needs no add=1 quote (see isReauthOfConnectedAccount,
    // socialAddon.js). Routing this through handleConnect (as round 2
    // mistakenly did, reading the CONTRACT UPDATE's "revoked account"
    // language too broadly) could pop the SocialConnectConfirm/cap dialog
    // for a plain re-auth the backend itself never gates. A genuinely
    // revoked/disconnected account has no row here to reconnect from at
    // all — Misc.jsx's social-page is the surface that can bring one of
    // those back, and IT branches on the raw status for exactly this
    // reason.
    //
    // TASK-329 round 4 fix (guard contract, 2nd review pass): also pass
    // reconnect: true through to api.socialAccounts.connect — the
    // auth-worker guard exempts the ack requirement for reconnect=1 ONLY
    // when the org already has a row for this platform, which is exactly
    // this button's case.
    startConnect(platform, { reconnect: true });
  };

  // A verified OAuth return / persisted permission failure names the grant as
  // missing for this platform (optionally one account).
  const metricsGrantContextFor = (account) => String(account.platform || '').toLowerCase() === metricsGrantContext?.platform
    && (!metricsGrantContext.accountId || account.id === metricsGrantContext.accountId);
  const handleMetricsReconnect = (account) => {
    // This action is contextual: a verified OAuth return says the grant was
    // declined, or (GH-794) the server says this account's active token lacks
    // the statistics scopes — and the server says this app can actually request
    // it. Missing capability data never becomes a reconnect prompt.
    const platform = String(account.platform || '').toLowerCase();
    if (!(metricsGrantContextFor(account) || account.metricsScopesGranted === false)
      || !metricsConsentPlatforms.has(platform)) return;
    setMetricsConsentAccount(account);
  };
  const confirmMetricsReconnect = async () => {
    const account = metricsConsentAccount;
    setMetricsConsentStarting(true);
    const result = await startConnect(account.platform, { reconnect: true, metrics: true, accountId: account.id });
    // The tab is already navigating to the provider: keep the dialog in its
    // "Opening …" state until it leaves; close it only when the start failed.
    if (result?.redirected === true) return;
    closeMetricsConsent();
  };
  const closeMetricsConsent = () => {
    setMetricsConsentStarting(false);
    setMetricsConsentAccount(null);
  };

  // Instagram now connects ONLY through direct Instagram Login (handleConnect
  // → /oauth/instagram/start). The old "via Facebook Page" path was removed
  // everywhere — direct connect covers all users with the approved
  // instagram_business_* permissions.

  return (
    <>
      {notice && (
        <div
          className={`s-notice s-notice-${notice.kind}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
          style={{
            padding: '12px 14px',
            marginBottom: 16,
            borderRadius: 'var(--r-md)',
            border: `1px solid ${notice.kind === 'error' ? 'var(--danger)' : 'var(--success)'}`,
            background: notice.kind === 'error' ? 'var(--danger-soft)' : 'var(--success-soft)',
            color: notice.kind === 'error' ? 'var(--danger-text, var(--danger))' : 'var(--success-text, var(--success))',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          {notice.kind === 'error'
            ? <I.alert size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            : <I.check size={16} style={{ flexShrink: 0, marginTop: 2 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{notice.title}</div>
            {notice.body && <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{notice.body}</div>}
            {notice.cta?.to ? (
              <button
                type="button"
                className="btn btn-soft btn-sm"
                onClick={() => {
                  try { track('change_plan_click', { source: 'oauth_redirect_notice' }); } catch (_) { /* fail-silent */ }
                  navigate(notice.cta.to);
                }}
                style={{ marginTop: 10, display: 'inline-flex' }}
              >
                {notice.cta.label}
              </button>
            ) : notice.cta?.href && (
              <a
                className="btn btn-soft btn-sm"
                href={notice.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: 10, display: 'inline-flex' }}
              >
                {notice.cta.label} <I.external size={11} style={{ marginLeft: 6 }} />
              </a>
            )}
          </div>
          <button
            className="btn btn-ghost btn-sm btn-icon"
            type="button"
            onClick={() => setNotice(null)}
            aria-label={t('settings.dismiss')}
            style={{ flexShrink: 0 }}
          >
            <I.x size={13} />
          </button>
        </div>
      )}
      <section className="s-card">
        <div className="s-card-head">
          <h3>{t('settings.connectedSocialAccounts')}</h3>
        </div>
        <div className="s-card-body">
          {loading ? (
            <div className="muted">{t('settings.loadingAccounts')}</div>
          ) : accounts.length === 0 ? (
            <p className="muted">{t('settings.noAccountsConnected')}</p>
          ) : (
            <ul className="account-list">
              {accounts.map((a) => {
                const platform = (a.platform || '').toLowerCase();
                const needsReconnect = a.status === 'needs_reconnect'
                  || a.status === 'expired'
                  || a.status === 'failed'
                  || (platform === authReconnectContext?.platform && a.id === authReconnectContext.accountId);
                // GH-794: the app can request statistics for this connected
                // account; flag only a stored grant that lacks the read scopes
                // and, when off (or a verified failure says so), show the
                // consent button. No "on" label: other networks have no such
                // hint, so it would wrongly suggest they lack statistics.
                const metricsConsentable = canRequestMetricsConsent(a, metricsConsentPlatforms);
                const metricsOff = metricsConsentMissing(a, metricsConsentPlatforms);
                const showMetricsConsent = metricsConsentable && (metricsOff || metricsGrantContextFor(a));
                return (
                  <li key={a.id} className="account-row" data-platform={platform}>
                    <span
                      className={`ob-net-ic-${platform}`}
                      style={{ width: 32, height: 32, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                    >
                      {BrandIcons[platform]?.({ size: 16 })}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="account-name">{a.displayName || a.externalAccountId || platformLabel(platform)}</div>
                      <div className="account-sub">
                        {platformLabel(platform)}
                        {a.status && a.status !== 'connected' && ` · ${a.status}`}
                      </div>
                      {/* TASK-041: say WHY and SINCE WHEN, not just "reconnect".
                          "Access expired 3 days ago" is actionable; a bare status
                          word leaves the user guessing whether they broke something. */}
                      {needsReconnect && (a.statusReason || a.statusChangedAt) && (
                        <div className="account-sub account-sub--warn">
                          {a.statusReason && t(`settings.reconnectReason.${String(a.statusReason).split(':')[0]}`, {
                            defaultValue: t('settings.reconnectReason.unknown')
                          })}
                          {a.statusChangedAt && ` · ${new Date(a.statusChangedAt).toLocaleDateString()}`}
                        </div>
                      )}
                      {metricsOff && (
                        <span className="pill warning" data-metrics-state="off" style={{ marginRight: 8 }}>{t('socialMetrics.accountMetricsOff')}</span>
                      )}
                      {showMetricsConsent && (
                          <button className="btn btn-ghost btn-sm" type="button" disabled={connecting === platform}
                            onClick={() => handleMetricsReconnect(a)}>
                            {t('socialMetrics.reconnectMetrics')}
                          </button>
                      )}
                    </div>
                    {needsReconnect ? (
                      <button
                        className="btn btn-soft btn-sm"
                        onClick={() => handleReconnect(a)}
                        disabled={connecting === platform}
                        type="button"
                      >
                        {t('settings.reconnect')}
                      </button>
                    ) : (
                      <span className="pill success"><span className="dot" />{t('settings.connected')}</span>
                    )}
                    {/* Per-account publish language. Growth+ → native dropdown.
                        Free/starter → locked chip routes to billing. When
                        publishLanguage is null we show the org default in
                        parens so the user sees what'll actually ship. */}
                    <AccountLanguageChip
                      value={a.publishLanguage}
                      orgDefault={defaultLanguage}
                      canOverride={canOverrideLanguage}
                      isPosting={dash.entitlements?.plan === 'posting'}
                      saving={languageSavingId === a.id}
                      onChange={(lang) => handleLanguageChange(a.id, lang)}
                      onUpgrade={() => navigate('/app/settings?tab=billing')}
                    />
                    {/* IG Direct Messages button removed — the linked
                        /app/messages screen crashed with React error
                        #130 (undefined component) and the feature
                        isn't shipped to users yet. The DM backend
                        (auth + content-worker manage_messages scope)
                        stays in place for when the screen is restored. */}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDisconnect(a.id)}
                      type="button"
                    >
                      {t('settings.disconnect')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="s-card">
        <div className="s-card-head">
          <h3>{t('settings.connectNewAccount')}</h3>
          {socialCapacity.limit != null && (
            <>
              <div className="sp" />
              <span className="muted" style={{ fontSize: 12 }}>
                {t('settings.socialLimitUsedLabel', { used: socialCapacity.used, limit: socialCapacity.limit })}
              </span>
            </>
          )}
        </div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            {t('settings.connectNewAccountDesc')}
          </p>
          {/* TASK-297: warn BEFORE the attempt, not after a redirect that
              dead-ends on auth-worker's QUOTA_EXCEEDED. Reconnecting an
              EXISTING account (the "Reconnect" button above, in the
              connected-accounts list) is unaffected — it's a different
              control and the backend never gates it by this quota.
              TASK-329 (owner decision, item 6): a billable org (can be
              charged the Scheduler add-on for the overage) no longer hits
              this hard block at all — handleConnect above routes it through
              <SocialConnectConfirm> instead. The banner + disabled grid stay
              exactly as before for free/no-card/lapsed orgs. */}
          {capacityBlocked && (
            <div
              style={{
                marginBottom: 14, padding: '10px 13px', borderRadius: 10,
                background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.40)',
                fontSize: 13, color: 'var(--text, inherit)', lineHeight: 1.45,
              }}
            >
              {t('settings.socialLimitReachedHint')}
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    try { track('change_plan_click', { source: 'connect_surface' }); } catch (_) { /* fail-silent */ }
                    navigate('/app/plans');
                  }}
                >
                  {t('settings.changePlan')}
                </button>
              </div>
            </div>
          )}
          <div className="connect-grid">
            {CONNECTABLE_PLATFORMS.flatMap((p) => {
              const platformBtn = (
                <button
                  key={p.id}
                  className="connect-btn"
                  data-platform={p.id}
                  onClick={() => { if (!p.soon && !capacityBlocked) handleConnect(p.id); }}
                  disabled={connecting === p.id || !!p.soon || capacityBlocked}
                  type="button"
                  title={p.soon ? t('settings.comingSoon') : capacityBlocked ? t('settings.socialLimitReachedHint') : undefined}
                >
                  <span
                    className={`ob-net-ic-${p.id}`}
                    style={{ width: 26, height: 26, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                  >
                    {BrandIcons[p.id]?.({ size: 13 })}
                  </span>
                  <span style={{ flex: 1 }}>{p.name}</span>
                  {p.soon ? (
                    <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{t('settings.soon')}</span>
                  ) : (
                    <>
                      {connectedPlatforms.has(p.id) && (
                        <span style={{ fontSize: 11, color: 'var(--success-text)' }}>·  {t('settings.linked')}</span>
                      )}
                      {connecting === p.id ? '…' : <I.arrowRight size={12} />}
                    </>
                  )}
                </button>
              );
              // Instagram connects via direct Instagram Login only (the single
              // "Instagram" button above) — the via-Facebook-Page option was removed.
              return [platformBtn];
            })}
          </div>
        </div>
      </section>
      <SocialConnectConfirm
        platform={socialConnectPlatform}
        entitlements={dash.entitlements}
        onCancel={() => setSocialConnectPlatform(null)}
        onContinue={(meta) => {
          const platform = socialConnectPlatform;
          setSocialConnectPlatform(null);
          startConnect(platform, meta);
        }}
      />
      <MetricsConsentDialog
        account={metricsConsentAccount}
        confirming={metricsConsentStarting}
        onConfirm={confirmMetricsReconnect}
        onCancel={closeMetricsConsent}
      />
    </>
  );
}

export function MonthlyOperationCard({ entitlements, onActivateTrialNow, activatingTrial }) {
  const { t } = useTranslation();
  const ownPublishing = freePublishingState(entitlements);
  const accessState = effectiveAccessState(entitlements);
  const lapsed = accessState === 'lapsed';
  const usage = entitlements?.usage || {};
  // Backend returns `billingStatus` (not `subscriptionStatus`).
  const billingStatus = entitlements?.billingStatus || entitlements?.subscriptionStatus || null;
  const currentPlanId = resolveCurrentPlanId(entitlements);
  const currentPlanIsNew = currentPlanId ? NEW_PLAN_DEFS.some((p) => p.id === currentPlanId) : false;
  const currentPlanTextKeys = currentPlanId
    ? (currentPlanIsNew ? NEW_PLAN_TEXT_KEYS[currentPlanId] : PLAN_TEXT_KEYS[currentPlanId])
    : null;
  const planName = entitlements?.planName
    || (currentPlanId && currentPlanTextKeys ? t(`settings.${currentPlanTextKeys.name}`) : null)
    || t('settings.planFree');

  if (ownPublishing) return <section className="s-card">
    <div className="s-card-head"><h3>{t('settings.thisMonthsOperation')}</h3></div>
    <div className="s-card-body"><FreePublishingAllowance capability={ownPublishing} /></div>
  </section>;

  return (
    <section className="s-card">
      <div className="s-card-head">
        <h3>{t('settings.thisMonthsOperation')}</h3>
      </div>
      <div className="s-card-body">
        {/* Trial clarity: the plan name above reads "Brand Pilot", but during
            the trial the limits below are free-tier. Make it explicit they
            grow to the full plan after the first real payment. */}
        {(!accessState || lapsed) && <CreationAccessNotice />}
        {!lapsed && billingStatus === 'trialing' && (
          <div
            style={{
              marginBottom: 14, padding: '10px 13px', borderRadius: 10,
              background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.40)',
              fontSize: 13, color: 'var(--text, inherit)', lineHeight: 1.45,
            }}
          >
            {t('settings.trialLimitsNote', { plan: planName || 'Brand Pilot' })}
            <div style={{ marginTop: 10 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onActivateTrialNow(planName)}
                disabled={activatingTrial}
                type="button"
              >
                {activatingTrial ? t('settings.activatingTrialNow') : t('settings.activateTrialNow')}
              </button>
            </div>
          </div>
        )}
        <div className="usage-grid">
          {(() => {
            const metrics = [
              ['publish_ready_posts_monthly', t('settings.usagePosts')],
              ['manual_posts_monthly', t('settings.usageManualPosts')],
              ['caption_assists_monthly', t('settings.usageCaptionAssists')],
              ['carousel_generations_monthly', t('settings.usageCarousels')],
              ['premium_visuals_monthly', t('settings.usageAiImages')],
              ['content_plan_regenerations_monthly', t('settings.usageContentPlanRegenerations')],
              ['assistant_turns_monthly', t('settings.usageAssistantMessages')],
            ];
            // Only metrics with a real cap get a meter — an unlimited (∞) row is
            // noise (you can never hit it). On paid plans most are unlimited, so
            // they collapse into one quiet line; free plans show their real caps.
            // TASK-303/314: on the Scheduler (posting) plan specifically, a limit
            // of exactly 0 means the plan does not include this metric at all
            // (not "you used up a real allowance") — showing "0 / 0" reads as
            // broken there, so those rows are hidden entirely.
            //
            // fix #8 (judge review): that hiding must stay SCOPED to `posting` —
            // the original filter dropped every 0-limit row on every plan, which
            // silently changed free-no-card's usage card (it legitimately shows
            // its real 0 caps, e.g. before a trial starts). Every other plan
            // keeps the pre-TASK-303/314 behaviour: `limit != null` only.
            const isSchedulerPlan = entitlements?.plan === 'posting';
            const finite = metrics.filter(([k]) => usage[k] && (lapsed || (usage[k].limit != null && (isSchedulerPlan ? usage[k].limit > 0 : true))));
            const unlimited = lapsed ? [] : metrics.filter(([k]) => usage[k] && usage[k].limit == null);
            return (
              <>
                {finite.map(([key, label]) => {
                  const data = usage[key];
                  const used = data?.used ?? 0;
                  const limit = data?.limit;
                  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                  return (
                    <div key={key} className="usage-cell">
                      <div className="lbl">{label}</div>
                      <div className="v">{lapsed ? used : <>{used} / {limit}</>}</div>
                      {!lapsed && limit > 0 && (<div className="pr"><div className="pr-fill" style={{ width: pct + '%' }} /></div>)}
                      {!lapsed && data?.remaining != null && (<div className="sub">{t('settings.nRemaining', { count: data.remaining })}</div>)}
                    </div>
                  );
                })}
                {unlimited.length > 0 && (
                  <div className="usage-unl">
                    <span className="usage-unl-l">{t('settings.unlimited')}</span>
                    {unlimited.map(([, l]) => l).join(' · ')}
                  </div>
                )}
                {Object.keys(usage).length === 0 && (
                  <>
                    <div className="usage-cell"><div className="lbl">{t('settings.strategyRefresh')}</div><div className="v">— / —</div><div className="sub">{t('settings.awaitingEntitlements')}</div></div>
                    <div className="usage-cell"><div className="lbl">{t('settings.contentBatches')}</div><div className="v">— / —</div><div className="sub">{t('settings.awaitingEntitlements')}</div></div>
                    <div className="usage-cell"><div className="lbl">{t('settings.visualConcepts')}</div><div className="v">— / —</div><div className="sub">{t('settings.awaitingEntitlements')}</div></div>
                  </>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Billing tab ---------------- */
export function BillingTab({
  entitlements,
  planKnown = true,
  onActivateTrialNow,
  activatingTrial,
  onRefreshEntitlements,
  captureEntitlementsScope,
  isEntitlementsScopeCurrent,
  accountId,
  resumeSubmitted,
  isResumeSubmitted,
  markResumeSubmitted,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { openUpgrade } = useUpgrade();
  const { getPlanDisplay, getNewPlanDisplay } = usePlanCatalog();
  const ownPublishing = planKnown ? freePublishingState(entitlements) : null;
  const effectiveFree = Boolean(ownPublishing) || (effectiveAccessState(entitlements) === 'free' && entitlements?.effectivePlan === 'free');
  const currentPlanId = resolveCurrentPlanId(effectiveFree ? null : entitlements);
  // A currentPlanId can come from either grid — look it up in whichever one
  // actually has it. NEW_PLAN_DEFS is checked first only because it's cheap
  // and unambiguous (identity ids); legacy PLAN_DEFS ids never collide with
  // it in practice since resolveCurrentPlanId already disambiguated by key.
  const currentPlanIsNew = currentPlanId ? NEW_PLAN_DEFS.some((p) => p.id === currentPlanId) : false;
  const currentPlanDef = currentPlanId
    ? (currentPlanIsNew ? NEW_PLAN_DEFS.find((p) => p.id === currentPlanId) : PLAN_DEFS.find((p) => p.id === currentPlanId))
    : null;
  const currentPlanTextKeys = currentPlanId
    ? (currentPlanIsNew ? NEW_PLAN_TEXT_KEYS[currentPlanId] : PLAN_TEXT_KEYS[currentPlanId])
    : null;
  // DB-backed display catalog (docs/TARIFF-REWORK-BRIEF.md §3/§4): falls back
  // to the exact hardcoded PLAN_DEFS/NEW_PLAN_DEFS entry whenever the server
  // field is null/missing or the fetch hasn't resolved yet. Only meaningful
  // for a self-service plan with a real featured price; the legacy
  // "soon"/"contact" tiers have no numeric price to show here.
  const currentPlanDisplay = currentPlanId
    ? (currentPlanIsNew ? getNewPlanDisplay(currentPlanId) : getPlanDisplay(currentPlanId))
    : null;
  // Every new-grid plan (starter/growth/pro/agency) is a real, self-service,
  // numerically-priced plan — unlike the legacy grid, where non-featured
  // means "soon"/"contact" with no numeric price. So the gate here is
  // `featured` for legacy (unchanged) OR simply "is a new-grid plan".
  const currentPlanPriceLine = ((currentPlanDef?.featured || currentPlanIsNew) && currentPlanDisplay?.priceFormatted)
    ? `${currentPlanDisplay.priceFormatted}${currentPlanDisplay.unit || ''}`
    : null;

  // Logic flag: is this effectively the Free plan? Computed from backend /
  // resolved id, independent of any translation, so the `Operator-level
  // scope` pill below keeps its original behavior.
  const isFreePlan = !currentPlanId && !entitlements?.planName;
  // Display name: backend name (a product name, identical across locales) →
  // translated plan name → translated "Free".
  const planName = effectiveFree ? t('settings.planFree') : entitlements?.planName
    || (currentPlanId && currentPlanTextKeys ? t(`settings.${currentPlanTextKeys.name}`) : null)
    || t('settings.planFree');
  // Description: prefer the LOCALIZED i18n copy for a known plan. The backend's
  // `planDescription` comes from the billing_plans DB seed and is English-only,
  // so it must NOT win over the translation — keep it only as a fallback for a
  // plan we don't have a local key for.
  const planDescription = effectiveFree ? t('settings.freePlanDesc') :
    (currentPlanId && currentPlanTextKeys?.desc
      ? t(`settings.${currentPlanTextKeys.desc}`)
      : null)
    || entitlements?.planDescription
    || t('settings.freePlanDesc');
  // TASK-328 fix (judge critic, blocker item 1): the actual "on a paid AI
  // tier" signal — see lib/postingUpsell.js's header for why
  // `features.scheduler` alone can't be used for this (it's true on free too).
  const paidAiPlan = !ownPublishing && entitlements?.features?.strategy_editing === true;
  // TASK-329 round 2 fix (blocker 1): resolved through resolveSocialAddonState
  // — the ONE mode gate — before building the pricing/copy, so this screen
  // can never disagree with AccountsTab's own capacityBlocked/handleConnect
  // logic about whether the org is 'hidden' (render nothing new)/'includes'
  // (plain line only, no price)/'priced' (full provenance+chip+sheet).
  // `mode: 'hidden'` renders null (SocialAddonProvenanceLine renders
  // nothing) — no org context, reason 'flag_off', or the best-effort
  // backend lookup failed — same fail-soft-to-hidden contract as every
  // other best-effort field on that response.
  const billingStatus = entitlements?.billingStatus || entitlements?.subscriptionStatus || null;
  const socialAddonState = resolveSocialAddonState(entitlements?.socialAccounts);
  const socialAddonPricing = socialAddonState.mode === 'hidden'
    ? null
    : resolveSocialAddonPricing({
        mode: socialAddonState.mode,
        connected: socialAddonState.connected,
        included: socialAddonState.included,
        unitAmount: socialAddonState.unitAmount,
        currency: socialAddonState.currency,
        planName,
        locale: i18n.language || 'en',
        t,
      });
  const [accountsSheetOpen, setAccountsSheetOpen] = useState(false);
  const socialAddonTriggerRef = useRef(null);
  const nextInvoiceAt = entitlements?.nextInvoiceAt || entitlements?.currentPeriodEnd;
  // Effective lifecycle, never the historical catalog plan, controls access.
  const accessState = effectiveAccessState(entitlements);
  const lapsed = accessState === 'lapsed' && !ownPublishing;
  const hasActiveSubscription = ACCESS_STATES_WITH_ACTIVE_SUBSCRIPTION.has(accessState);
  const billingKnown = planKnown && Boolean(accessState);

  // Subscription scheduled to cancel at period end (Stripe cancel_at_period_end).
  // Status stays "active" until then, so we surface this explicitly — otherwise
  // a successful cancel looks like nothing happened.
  const cancelAtPeriodEnd = canResumeSubscription(entitlements);
  const cancelDateLabel = nextInvoiceAt
    ? new Date(nextInvoiceAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })
    : t('settings.endOfCurrentPeriod');

  // Owner policy: unpaid must be loudly visible. Additive field on
  // /api/billing/entitlements — non-null while the last payment attempt on
  // the subscription failed (Stripe past_due/incomplete/unpaid). The
  // accessState distinguishes temporary grace from a lapse; payment recovery
  // stays visible alongside either state.
  const paymentIssue = entitlements?.paymentIssue || null;
  // GH-912: subscription sold through Link (Stripe Managed Payments). Card
  // changes go through the Stripe Customer Portal (PaymentMethodCard) and
  // invoices/receipts are issued and e-mailed by Link. Cancel/resume/change
  // plan are unchanged — they still run through our own billing handlers.
  const managedPayments = isManagedPaymentsSubscription(entitlements);

  // Invoice rows: label from the plan the invoice ACTUALLY billed (backend
  // plan key, stamped by the webhook + 0089 backfill) — never the account's
  // current plan, which mislabeled history after an upgrade.
  const invoicePlanLabel = (key) => {
    if (!key) return null;
    const newId = NEW_PLAN_ID_BY_KEY?.[key];
    if (newId && NEW_PLAN_TEXT_KEYS[newId]) return t(`settings.${NEW_PLAN_TEXT_KEYS[newId].name}`);
    const id = PLAN_ID_BY_KEY?.[key];
    if (id && PLAN_TEXT_KEYS[id]) return t(`settings.${PLAN_TEXT_KEYS[id].name}`);
    return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // TASK-331 phase 2 (Subscription Schedules) — a downgrade to a LOWER plan
  // is scheduled for a future date; the CURRENT (higher) plan/limits stay
  // active until then (accessState 'downgrade_scheduled', entitlement-types.ts).
  // Cancel wins over this the same way it wins in the backend's accessState
  // order (grace -> cancel_scheduled -> downgrade_scheduled -> active): a
  // subscription that is ALSO ending outright is a more urgent fact than a
  // future plan change on it, so this notice never renders next to the amber
  // cancellation one above.
  const scheduledDowngrade = hasActiveSubscription && !cancelAtPeriodEnd ? resolveScheduledDowngrade(entitlements) : null;
  // MINOR 2 (round 7 review): 'posting' has no billing_plans-keyed display
  // name — invoicePlanLabel's own fallback just title-cases the raw key
  // ("Posting"), instead of the actual product name ("Scheduler"). Check it
  // first, same as Plans.jsx's resolvePlanDisplayName (~L896) does for the
  // identical scheduled-downgrade badge on that screen.
  const scheduledPlanName = scheduledDowngrade
    ? (scheduledDowngrade.plan === 'posting' ? t('postingPlan.name') : invoicePlanLabel(scheduledDowngrade.plan))
    : null;
  const scheduledDateLabel = scheduledDowngrade
    ? new Intl.DateTimeFormat(i18n.language || 'en', { dateStyle: 'medium' }).format(new Date(scheduledDowngrade.effectiveAt))
    : null;
  const [undoingDowngrade, setUndoingDowngrade] = useState(false);
  const [undoDowngradeError, setUndoDowngradeError] = useState(null);
  const undoIdentity = JSON.stringify([accountId, scheduledDowngrade?.plan, scheduledDowngrade?.effectiveAt]);
  const [undoSubmittedFor, setUndoSubmittedFor] = useState(null);
  useEffect(() => {
    setUndoingDowngrade(false);
    setUndoDowngradeError(null);
  }, [accountId, scheduledDowngrade?.plan, scheduledDowngrade?.effectiveAt]);
  useEffect(() => {
    // A confirmed removal ends the identity even if a later schedule reuses
    // the same target/date. Unknown reads must retain the accepted marker.
    if (billingKnown && !resolveScheduledDowngrade(entitlements)) setUndoSubmittedFor(null);
  }, [billingKnown, entitlements]);
  const handleUndoScheduledDowngrade = async (quotedScheduleId) => {
    if (undoingDowngrade) return;
    // Scheduler's validated quote exposes the schedule instance. A missing
    // quote during reconciliation must not repeat an already accepted POST.
    const scheduleId = typeof quotedScheduleId === 'string' ? quotedScheduleId : null;
    const undoSubmitted = undoSubmittedFor?.identity === undoIdentity
      && (!scheduleId || !undoSubmittedFor.scheduleId || undoSubmittedFor.scheduleId === scheduleId);
    const scope = captureEntitlementsScope?.();
    const isCurrent = () => !scope || isEntitlementsScopeCurrent?.(scope);
    setUndoingDowngrade(true);
    setUndoDowngradeError(null);
    try {
      if (!undoSubmitted) {
        await api.billing.cancelScheduledDowngrade();
        if (!isCurrent()) return;
        setUndoSubmittedFor({ identity: undoIdentity, scheduleId });
      }
      // Silent refetch (no window.alert / reload, unlike handleResumeSubscription
      // above) — once entitlements.scheduledPlan clears, this notice un-renders
      // on its own, which IS the success confirmation.
      const result = await onRefreshEntitlements?.({ scope, until: next => !resolveScheduledDowngrade(next) });
      if (isCurrent() && result && !result.converged && !result.cancelled) setUndoDowngradeError(t('settings.billingStatusPending'));
    } catch (err) {
      if (!isCurrent()) return;
      console.error('Billing action failed', { type: 'undoScheduledDowngrade', err });
      const msg = errMessage(err);
      const code = err?.code ? ` (${err.code})` : '';
      setUndoDowngradeError(t('settings.billingActionFailed', { code, message: msg }));
    } finally {
      if (isCurrent()) setUndoingDowngrade(false);
    }
  };

  const [invoices, setInvoices] = useState([]);
  const [invoicesExpanded, setInvoicesExpanded] = useState(false);
  // The saved card now lives in its own <PaymentMethodCard> section (display +
  // change-card), which owns its own fetch — Settings no longer tracks card state.
  useEffect(() => {
    let cancelled = false;
    setInvoices([]);
    api.billing.getInvoices()
      .then((res) => {
        if (cancelled) return;
        setInvoices(res?.invoices || res?.data?.invoices || []);
      })
      .catch(() => { /* endpoint may be empty for free plan */ });
    return () => { cancelled = true; };
  }, [accountId]);

  // Confirmation modal state for plan change / cancel / checkout
  const [confirm, setConfirm] = useState(null); // { type, targetId, title, description, confirmLabel, tone, action }
  const [busy, setBusy] = useState(false);

  const closeConfirm = () => { if (!busy) setConfirm(null); };

  const handleConfirm = async () => {
    if (!confirm || busy || !billingKnown) return;
    setBusy(true);
    try {
      await confirm.action();
    } catch (err) {
      console.error('Billing action failed', { type: confirm?.type, targetId: confirm?.targetId, err });
      const msg = errMessage(err);
      const code = err?.code ? ` (${err.code})` : '';
      window.alert(t('settings.billingActionFailed', { code, message: msg }));
      setBusy(false);
      return;
    }
    setBusy(false);
    setConfirm(null);
  };

  const openCancelConfirm = () => {
    const accessEnd = accessState === 'grace' ? entitlements?.graceEndsAt : nextInvoiceAt;
    const until = accessEnd
      ? new Date(accessEnd).toLocaleDateString(i18n.language, { dateStyle: 'medium' })
      : t('settings.endOfCurrentBillingPeriod');
    setConfirm({
      type: 'cancel',
      tone: 'danger',
      title: t('settings.cancelSubscriptionTitle'),
      description: t('settings.cancelSubscriptionDesc', { until }),
      confirmLabel: t('settings.cancelSubscriptionConfirm'),
      cancelLabel: t('settings.keepSubscription'),
      action: async () => {
        await api.billing.cancelSubscription({ cancelAtPeriodEnd: true });
        window.location.reload();
      },
    });
  };

  // Un-cancel a subscription scheduled to cancel at period end. Positive,
  // safe, reversible action — no confirm modal (unlike cancel above).
  const [resuming, setResuming] = useState(false);
  const [resumeError, setResumeError] = useState(null);

  useEffect(() => {
    setResuming(false);
    setResumeError(null);
  }, [accountId]);

  useEffect(() => {
    if (subscriptionResumeHasConverged(entitlements)) {
      setResumeError(null);
    }
  }, [entitlements]);

  const handleResumeSubscription = async () => {
    if (resuming) return;
    const scope = captureEntitlementsScope?.();
    setResuming(true);
    setResumeError(null);
    try {
      if (!isResumeSubmitted(scope)) {
        await api.billing.resumeSubscription();
        if (scope && !isEntitlementsScopeCurrent?.(scope)) return;
        markResumeSubmitted(scope);
      }
      const result = await onRefreshEntitlements?.({
        until: subscriptionResumeHasConverged,
        scope,
      });
      if (scope && !isEntitlementsScopeCurrent?.(scope)) return;
      if (result && !result.converged && !result.cancelled) setResumeError(t('settings.billingStatusPending'));
    } catch (err) {
      if (scope && !isEntitlementsScopeCurrent?.(scope)) return;
      console.error('Billing action failed', { type: 'resume', err });
      const msg = errMessage(err);
      const code = err?.code ? ` (${err.code})` : '';
      setResumeError(t('settings.billingActionFailed', { code, message: msg }));
    } finally {
      if (!scope || isEntitlementsScopeCurrent?.(scope)) setResuming(false);
    }
  };

  // The full tariff grid now lives on the dedicated /app/plans page — the
  // "Change plan" button navigates there instead of scrolling to an embedded
  // grid (which was removed from this tab).
  const goToPlans = () => navigate('/app/plans');

  return (
    <>
      <div className="head">
        <h2>{t('settings.planAndBilling')}</h2>
        <p className="lede">
          {t('settings.billingLede')}
        </p>
      </div>

      {/* Payment-required alert: full-width, ABOVE the two-column plan card —
          inside the left column it renders squeezed next to the actions column. */}
      {!billingKnown && <CreationAccessNotice />}
      {billingKnown && (isDunningPaymentIssue(paymentIssue) || (lapsed && paymentIssue?.invoiceUrl)) && (
        <PaymentIssueNotice paymentIssue={paymentIssue} planName={planName} graceEndsAt={accessState === 'grace' ? entitlements?.graceEndsAt : null} dismissible={false} />
      )}

      {billingKnown && !lapsed && !effectiveFree && <PostingSubscriptionCard entitlements={entitlements} />}

      {lapsed && <section className="current-plan">
        <div>
          <span className="tier">{t('lapsed.bannerTitle')}</span>
          <div className="name">{t('settings.inactiveAccess')}</div>
          <p className="desc">{t('paymentIssue.lapsedBody')}</p>
          {entitlements?.lapsedAt && <p className="desc">{t('lapsed.accessEnded', { date: new Date(entitlements.lapsedAt).toLocaleDateString(i18n.language, { dateStyle: 'medium' }) })}</p>}
          {paymentIssue?.reason === 'payment_failed' && <p className="desc">{t('lapsed.reasonPaymentFailed')}</p>}
          {paymentIssue?.reason === 'unpaid' && <p className="desc">{t('lapsed.reasonUnpaid')}</p>}
          <p className="desc">{t('settings.previousPlan', { plan: planName })}</p>
        </div>
        <button className="btn btn-primary btn-lg" type="button" onClick={() => openUpgrade({ feature: 'default', variant: 'renew' })}>
          {t('lapsed.renew')}
        </button>
      </section>}

      {/* Scheduler has its own quantity-aware current-subscription card below. */}
      {billingKnown && !lapsed && (effectiveFree || entitlements?.plan !== 'posting') && <section className="current-plan">
        <div>
          <span className="tier">{t('settings.currentPlan')}</span>
          <div className="name">{planName}</div>
          <p className="desc">{planDescription}</p>
          {/* TASK-328 fix (judge critic, blocker item 1): `features.scheduler`
              is true on FREE too (a capability flag — free can schedule up to
              its own small monthly quota), so this used to also claim
              "included in your plan" for a free org that hasn't paid for
              anything. `strategy_editing` is the flag that actually means
              "on a paid AI tier" (TASK-327 D-B). */}
          {paidAiPlan && !isPostingPlan(entitlements) && (
            <p className="desc" style={{ opacity: 0.85 }}>{t('settings.schedulerIncluded')}</p>
          )}
          <div className="meta">
            {currentPlanPriceLine && !nextInvoiceAt && (
              <span><b>{t('settings.price', { defaultValue: 'Price' })}</b> · {currentPlanPriceLine}</span>
            )}
            {accessState === 'grace' && entitlements?.graceEndsAt && (
              <span><b>{t('grace.accessUntil')}</b> · {new Date(entitlements.graceEndsAt).toLocaleDateString(i18n.language, { dateStyle: 'medium' })}</span>
            )}
            {!effectiveFree && accessState !== 'grace' && nextInvoiceAt && !cancelAtPeriodEnd && (
              <span><b>{t('settings.nextInvoice')}</b> · {new Date(nextInvoiceAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            )}
            {!effectiveFree && entitlements?.billingInterval && (
              <span><b>{t('settings.billingPeriod')}</b> · {entitlements.billingInterval}</span>
            )}
            <span><b>{t('settings.brands')}</b> · {t('settings.brandsScopeValue')}</span>
          </div>
          {/* TASK-329 (owner decision, item 1): price provenance for the
              CURRENT plan — "N connected · <Plan> includes K -> +(N-K) x
              €3.99 = €X/mo", or the plain "<Plan> includes K" once nothing
              is billed beyond it. The "N connected" segment only becomes a
              button (opens the sheet) once pricing.extra > 0. */}
          <div ref={socialAddonTriggerRef}>
            <SocialAddonProvenanceLine pricing={socialAddonPricing} onOpenSheet={() => setAccountsSheetOpen(true)} />
            {/* MINOR 7 (round 2 fix): the confirm dialog already shows a
                trial no-charge-yet note for a NEW overage connect
                (confirmTrialNote) — this mirrors that same qualifier on the
                standing provenance line itself, so a card-backed trial org
                isn't only told about it mid-connect.
                Round 3 fix: gated on `mode === 'priced' && extra > 0`, not
                just "pricing object exists" — outside an actual overage
                there is no charge to reassure the trial org about (an
                'includes' org, or a priced org still within its included
                count, would otherwise see "No charge until your first
                payment" next to a line that was never going to charge
                anything either way). */}
            {socialAddonPricing?.mode === 'priced' && socialAddonPricing.extra > 0 && billingStatus === 'trialing' && (
              <p className="social-addon-line social-addon-trial-note">{t('socialAddon.provenanceTrialSuffix')}</p>
            )}
          </div>
          {cancelAtPeriodEnd && (
            <div
              style={{
                marginTop: 12, padding: '10px 13px', borderRadius: 10,
                background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)',
                fontSize: 13, color: 'var(--text, inherit)', lineHeight: 1.45,
              }}
            >
              {accessState === 'grace'
                ? t('grace.bannerBody', { date: entitlements.graceEndsAt ? new Date(entitlements.graceEndsAt).toLocaleDateString(i18n.language, { dateStyle: 'medium' }) : cancelDateLabel })
                : (<>{t('settings.subscriptionCancelsPrefix')} <b>{cancelDateLabel}</b>. {t('settings.subscriptionCancelsSuffix')}</>)}
              {resumeError && <div className="flb-error" role="alert" style={{ marginTop: 8 }}>{resumeError}</div>}
              <div style={{ marginTop: 10 }}>
                <button
                  className="btn btn-violet btn-sm"
                  onClick={handleResumeSubscription}
                  disabled={resuming}
                  type="button"
                >
                  {resumeSubmitted
                    ? t('settings.refreshBillingStatus')
                    : (resuming
                      ? t('settings.resuming', { defaultValue: 'Resuming…' })
                      : t('settings.resumeSubscription', { defaultValue: 'Resume subscription' }))}
                </button>
              </div>
            </div>
          )}
          {/* TASK-331 phase 2 (Subscription Schedules) — informational (blue,
              not amber: nothing is ending, the current plan is safe until the
              flip date), mirroring the cancelAtPeriodEnd block above 1:1
              (same padding/radius/font, --info tokens instead of the amber
              rgba literals). Only when a downgrade is actually scheduled AND
              there's no ALSO-scheduled cancellation (that block above already
              took this slot — accessState order: cancel_scheduled wins over
              downgrade_scheduled). */}
          {scheduledDowngrade?.plan === 'posting' ? (
            <ScheduledPostingChangeCard
              accountId={accountId}
              pendingPlan={scheduledDowngrade.plan}
              effectiveAt={scheduledDowngrade.effectiveAt}
              currentPlanName={planName}
              onUndo={handleUndoScheduledDowngrade}
              undoing={undoingDowngrade}
              undoError={undoDowngradeError}
            />
          ) : scheduledDowngrade && (
            <div
              style={{
                marginTop: 12, padding: '10px 13px', borderRadius: 10,
                background: 'var(--info-soft)', border: '1px solid var(--info)',
                fontSize: 13, color: 'var(--text, inherit)', lineHeight: 1.45,
              }}
            >
              <div><b>{t('settings.downgradeScheduledTitle', { plan: scheduledPlanName, date: scheduledDateLabel })}</b></div>
              <div>{t('settings.scheduledCurrentUntil', { currentPlan: planName })}</div>
              {undoDowngradeError && (
                <div role="alert" style={{ marginTop: 8, color: 'var(--danger-text, #b91c1c)' }}>{undoDowngradeError}</div>
              )}
              <div style={{ marginTop: 10 }}>
                <button
                  className="btn btn-secondary scheduled-posting-undo"
                  onClick={handleUndoScheduledDowngrade}
                  disabled={undoingDowngrade}
                  type="button"
                >
                  {t('settings.keepCurrentPlan', { currentPlan: planName })}{undoingDowngrade ? '…' : ''}
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {billingStatus === 'trialing' && (
            <button
              className="btn btn-primary btn-lg"
              onClick={() => onActivateTrialNow(planName)}
              disabled={activatingTrial}
              type="button"
            >
              {activatingTrial ? t('settings.activatingTrialNow') : t('settings.activateTrialNow')}
            </button>
          )}
          <button className="btn btn-violet btn-lg" onClick={goToPlans} type="button">
            {t('settings.changePlan')}
          </button>
          {hasActiveSubscription && !cancelAtPeriodEnd && (
            <button className="btn btn-ghost btn-sm" onClick={openCancelConfirm} type="button">
              {t('settings.cancelSubscription')}
            </button>
          )}
        </div>
      </section>}

      {/* Payment method — display the saved card + change it. Renders nothing for
          users with no live subscription. */}
      <PaymentMethodCard key={accountId} disabled={!billingKnown} managedPayments={managedPayments} onRefreshBilling={onRefreshEntitlements} />

      {/* Plans grid moved to the dedicated /app/plans page (reached via the
          "Change plan" button above). PLAN_DEFS stays imported for the
          current-plan resolution. */}

      {/* Invoices */}
      <section className="s-card">
        <div className="s-card-head">
          <h3>{t('settings.invoices')}</h3>
          <div className="sp" />
          {invoices.length > 5 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setInvoicesExpanded((v) => !v)} type="button">
              {invoicesExpanded ? t('settings.showRecent') : t('settings.viewAll', { count: invoices.length })}
            </button>
          )}
        </div>
        <div>
          {managedPayments && (
            <p className="muted s-invoices-mp-note" style={{ margin: 0, padding: '12px 22px', fontSize: 13, lineHeight: 1.45, borderBottom: '1px solid var(--border-subtle)' }}>
              {t('settings.invoicesManagedPaymentsNote')}
            </p>
          )}
          {invoices.length === 0 ? (
            <div className="s-empty">{t('settings.noInvoices')}</div>
          ) : (
            (invoicesExpanded ? invoices : invoices.slice(0, 5)).map((inv) => {
              // A €0 invoice (amount due AND paid both 0) is the trial's first
              // invoice — show "Free trial", not "Paid" (no real money moved).
              const isZeroInvoice = (inv.amountPaid ?? 0) === 0 && (inv.amountDue ?? 0) === 0;
              const invStatusLabel = isZeroInvoice
                ? t('settings.invoiceFreeTrial', 'Free trial · €0')
                : (inv.status === 'paid' ? t('settings.paid') : inv.status);
              return (
              <div key={inv.id || inv.number} className="invoice-row">
                <div className="inv-no">{inv.number || inv.id}</div>
                <div className="inv-mid">
                  <div className="inv-title">{invoiceDisplayTitle({ planKey: inv.plan, description: inv.description, fallback: invoicePlanLabel(inv.plan) || t('billing.invoiceGeneric'), t })}</div>
                  <div className="inv-sub">
                    {invStatusLabel} ·
                    {inv.createdAt ? ` ${new Date(inv.createdAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                </div>
                <span className="pill success"><span className="dot" />{invStatusLabel}</span>
                {inv.status === 'open' && inv.hostedInvoiceUrl && (
                  <a className="btn btn-violet btn-sm" href={inv.hostedInvoiceUrl} target="_blank" rel="noopener">
                    {t('settings.payInvoice', { defaultValue: 'Pay' })}
                  </a>
                )}
                {inv.pdfUrl && (
                  <a className="btn btn-ghost btn-sm" href={inv.pdfUrl} target="_blank" rel="noopener">
                    <I.download size={12} /> PDF
                  </a>
                )}
              </div>
              );
            })
          )}
        </div>
      </section>

      <ConfirmDialog
        open={billingKnown && !!confirm}
        tone={confirm?.tone}
        title={confirm?.title}
        description={confirm?.description}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel}
        confirming={busy}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />

      {/* TASK-329: opens from the "N connected" segment above (ONLY when
          socialAddonPricing.extra > 0 — SocialAddonProvenanceLine's own
          gate). A disconnect here changes the org's billed overage, so it
          refreshes entitlements the same way every other billing action on
          this tab does. */}
      <ConnectedAccountsSheet
        open={accountsSheetOpen}
        pricing={socialAddonPricing}
        anchorRef={socialAddonTriggerRef}
        onClose={() => setAccountsSheetOpen(false)}
        onConnectAnother={() => { setAccountsSheetOpen(false); navigate('/app/settings?tab=accounts'); }}
        onDisconnected={() => { if (typeof onRefreshEntitlements === 'function') onRefreshEntitlements(); }}
      />
    </>
  );
}

/* ---------------- Usage tab ---------------- */
// Research isn't announced yet — its quota row stays hidden until the same
// flags that reveal the Research tab are set (see ContentPlanScreen).
const RESEARCH_VISIBLE = (() => {
  try {
    return typeof window !== 'undefined' && (
      window.localStorage.getItem('dathent.research.enabled') !== '0'
      || window.localStorage.getItem('dathent.research.onboarding') === '1'
    );
  } catch (_) {
    return false;
  }
})();

function UsageTab({ entitlements, counts, onActivateTrialNow, activatingTrial }) {
  const { t } = useTranslation();
  // Keep the all-time content totals separate from the monthly entitlement
  // meters; dumping every raw entitlement metric here was confusing.
  return (
    <>
      <MonthlyOperationCard
        entitlements={entitlements}
        onActivateTrialNow={onActivateTrialNow}
        activatingTrial={activatingTrial}
      />
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.usageDetail')}</h3></div>
        <div className="s-card-body">
          <dl className="info-list">
            <div><dt>{t('settings.totalContentCreated')}</dt><dd>{counts?.all || 0}</dd></div>
            <div><dt>{t('settings.publishedPosts')}</dt><dd>{counts?.published || 0}</dd></div>
            <div><dt>{t('settings.drafts')}</dt><dd>{counts?.draft || 0}</dd></div>
          </dl>
        </div>
      </section>
    </>
  );
}

/* ---------------- Connectors tab (API keys / MCP) ---------------- */
// Scopes mirror McpPermission in packages/dathent-shared-types (src/index.ts).
// manage_rules is intentionally NOT listed here — it's a dead/unused permission
// and shouldn't be offered. publish_content stays selectable (the backend gates
// the actual tool call behind MCP_PUBLISH_ENABLED=false today) so a key created
// now already carries the scope for whenever that flag flips.
const MCP_SCOPES = ['read_profile', 'read_scheduler', 'read_content', 'write_content', 'approve_content', 'publish_content'];
// approve_content is deliberately NOT pre-checked — no live tool uses it yet
// (see the "not used by any tool yet" note rendered next to it below).
const DEFAULT_MCP_SCOPES = ['read_profile', 'read_scheduler', 'read_content', 'write_content'];
// Scopes that are forward-provisioned but not wired to any tool yet — shown
// with a muted "(not used by any tool yet)" suffix so users aren't asked to
// consent to dead permissions unknowingly. Stay selectable (see MCP_SCOPES
// comment above re: MCP_PUBLISH_ENABLED).
const UNUSED_MCP_SCOPES = new Set(['approve_content', 'publish_content']);
const SCOPE_LABEL_KEYS = {
  read_profile: 'scopeReadProfile',
  read_scheduler: 'scopeReadScheduler',
  read_content: 'scopeReadContent',
  write_content: 'scopeWriteContent',
  approve_content: 'scopeApproveContent',
  publish_content: 'scopePublishContent',
};
const SCOPE_DESC_KEYS = {
  read_profile: 'scopeReadProfileDesc',
  read_scheduler: 'scopeReadSchedulerDesc',
  read_content: 'scopeReadContentDesc',
  write_content: 'scopeWriteContentDesc',
  approve_content: 'scopeApproveContentDesc',
  publish_content: 'scopePublishContentDesc',
};
const CONNECTOR_MONO_FONT = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

function fmtDateShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

// Create-token expiry choices → ISO expiresAt (null = no expiry, unchanged
// default behavior so existing connectors never silently break).
function computeExpiresAt(choice) {
  if (choice === '30d' || choice === '90d') {
    const d = new Date();
    d.setDate(d.getDate() + (choice === '30d' ? 30 : 90));
    return d.toISOString();
  }
  if (choice === '1y') {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString();
  }
  return null;
}

function ConnectorsTab() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(true);
  const [grantsError, setGrantsError] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'success'|'error', title, body }
  const [urlCopied, setUrlCopied] = useState(false);
  const [commandCopied, setCommandCopied] = useState(false);
  const [codexAddCopied, setCodexAddCopied] = useState(false);
  const [codexLoginCopied, setCodexLoginCopied] = useState(false);

  // Create-token form
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('none'); // 'none' | '30d' | '90d' | '1y'
  const [scopes, setScopes] = useState(() => new Set(DEFAULT_MCP_SCOPES));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [reveal, setReveal] = useState(null); // { apiKey, key } — one-time raw token
  const [tokenCopied, setTokenCopied] = useState(false);

  // Revoke confirm
  const [revokeTarget, setRevokeTarget] = useState(null); // the ApiKey record
  const [revoking, setRevoking] = useState(false);
  const [grantRevokeTarget, setGrantRevokeTarget] = useState(null);
  const [grantRevoking, setGrantRevoking] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    return api.apiKeys.list()
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setKeys(Array.isArray(payload) ? payload : (payload?.keys || []));
      })
      .catch(() => setKeys([]))
      .finally(() => setLoading(false));
  }, []);

  const refreshGrants = useCallback(() => {
    setGrantsLoading(true);
    setGrantsError(false);
    return api.apiKeys.listGrants()
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setGrants(Array.isArray(payload) ? payload : (payload?.grants || []));
      })
      .catch(() => {
        setGrants([]);
        setGrantsError(true);
      })
      .finally(() => setGrantsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    refreshGrants();
  }, [refresh, refreshGrants]);

  const copyEndpoint = () => {
    navigator.clipboard?.writeText(MCP_ENDPOINT_URL).catch(() => {});
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 1800);
  };

  const copyClaudeCommand = () => {
    navigator.clipboard?.writeText(CLAUDE_CODE_COMMAND).catch(() => {});
    setCommandCopied(true);
    setTimeout(() => setCommandCopied(false), 1800);
  };

  const copyCodexAddCommand = () => {
    navigator.clipboard?.writeText(CODEX_ADD_COMMAND).catch(() => {});
    setCodexAddCopied(true);
    setTimeout(() => setCodexAddCopied(false), 1800);
  };

  const copyCodexLoginCommand = () => {
    navigator.clipboard?.writeText(CODEX_LOGIN_COMMAND).catch(() => {});
    setCodexLoginCopied(true);
    setTimeout(() => setCodexLoginCopied(false), 1800);
  };

  const copyToken = () => {
    if (!reveal?.apiKey) return;
    navigator.clipboard?.writeText(reveal.apiKey).catch(() => {});
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 1800);
  };

  const toggleScope = (scope) => {
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope); else next.add(scope);
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    if (scopes.size === 0) {
      setCreateError(t('settings.connectorScopeRequired', 'Select at least one permission.'));
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const res = await api.apiKeys.create({
        name: name.trim(),
        scopes: Array.from(scopes),
        expiresAt: computeExpiresAt(expiry),
      });
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      if (payload?.apiKey) {
        setReveal({ apiKey: payload.apiKey, key: payload.key });
      }
      setName('');
      setExpiry('none');
      setScopes(new Set(DEFAULT_MCP_SCOPES));
      await refresh();
    } catch (err) {
      setCreateError(errMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const closeReveal = () => setReveal(null);

  const openRevoke = (key) => setRevokeTarget(key);
  const closeRevoke = () => { if (!revoking) setRevokeTarget(null); };
  const confirmRevoke = async () => {
    if (!revokeTarget || revoking) return;
    setRevoking(true);
    try {
      await api.apiKeys.revoke(revokeTarget.id);
      setRevokeTarget(null);
      await refresh();
      setNotice({
        kind: 'success',
        title: t('settings.connectorTokenRevoked', 'Token revoked'),
        body: t('settings.connectorTokenRevokedBody', { name: revokeTarget.name, defaultValue: '"{{name}}" can no longer authenticate.' }),
      });
    } catch (err) {
      setNotice({ kind: 'error', title: t('settings.connectorRevokeFailed', "Couldn't revoke token"), body: errMessage(err) });
      setRevoking(false);
    }
  };

  const closeGrantRevoke = () => { if (!grantRevoking) setGrantRevokeTarget(null); };
  const confirmGrantRevoke = async () => {
    if (!grantRevokeTarget || grantRevoking) return;
    const target = grantRevokeTarget;
    const previousGrants = grants;
    setGrantRevoking(true);
    setGrants((current) => current.filter((grant) => grant.id !== target.id));
    try {
      await api.apiKeys.revokeGrant(target.id);
      setGrantRevokeTarget(null);
      await refreshGrants();
      setNotice({
        kind: 'success',
        title: t('settings.connectorGrantRevoked'),
        body: t('settings.connectorGrantRevokedBody', { name: target.clientName || target.clientId }),
      });
    } catch (_err) {
      setGrants(previousGrants);
      setNotice({
        kind: 'error',
        title: t('settings.connectorGrantRevokeFailed'),
        body: t('settings.connectorGrantRevokeFailedBody'),
      });
    } finally {
      setGrantRevoking(false);
    }
  };

  const statusPillProps = (status) => {
    if (status === 'active') return { cls: 'pill success', label: t('settings.connectorStatusActive', 'Active') };
    if (status === 'expired') return { cls: 'pill pill-warning', label: t('settings.connectorStatusExpired', 'Expired') };
    return { cls: 'pill pill-danger', label: t('settings.connectorStatusRevoked', 'Revoked') };
  };

  return (
    <>
      {notice && (
        <div
          className={`s-notice s-notice-${notice.kind}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
          style={{
            padding: '12px 14px',
            marginBottom: 16,
            borderRadius: 'var(--r-md)',
            border: `1px solid ${notice.kind === 'error' ? 'var(--danger)' : 'var(--success)'}`,
            background: notice.kind === 'error' ? 'var(--danger-soft)' : 'var(--success-soft)',
            color: notice.kind === 'error' ? 'var(--danger-text, var(--danger))' : 'var(--success-text, var(--success))',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          {notice.kind === 'error'
            ? <I.alert size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            : <I.check size={16} style={{ flexShrink: 0, marginTop: 2 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{notice.title}</div>
            {notice.body && <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{notice.body}</div>}
          </div>
          <button
            className="btn btn-ghost btn-sm btn-icon"
            type="button"
            onClick={() => setNotice(null)}
            aria-label={t('settings.dismiss')}
            style={{ flexShrink: 0 }}
          >
            <I.x size={13} />
          </button>
        </div>
      )}

      {/* Intro: what this is + connection methods for Claude/ChatGPT */}
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.connectorsIntroTitle', 'Connect Dathent to your AI assistant')}</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14, maxWidth: 640 }}>
            {t('settings.connectorsIntroDesc')}
          </p>
          <div className="connector-methods">
            <div className="connector-method">
              <div className="connector-method-info">
                <strong>{t('settings.connectorClaudeTitle')}</strong>
                <span className="muted">{t('settings.connectorClaudeInstallDesc')}</span>
              </div>
              <div className="connector-method-action">
                <a
                  className="btn btn-primary"
                  href={CLAUDE_INSTALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('settings.connectorClaudeInstall')} <I.external size={13} />
                </a>
              </div>
            </div>
            <div className="connector-method">
              <div className="connector-method-info">
                <strong>{t('settings.connectorClaudeCodeTitle')}</strong>
                <span className="muted">{t('settings.connectorClaudeCodeHint')}</span>
              </div>
              <div className="connector-method-action connector-method-action-code">
                <div className="connector-url-row">
                  <code className="connector-url-code" style={{ fontFamily: CONNECTOR_MONO_FONT }}>{CLAUDE_CODE_COMMAND}</code>
                  <button type="button" className={`btn btn-sm ${commandCopied ? 'btn-violet' : 'btn-ghost'}`} onClick={copyClaudeCommand}>
                    {commandCopied ? (<><I.check size={12} /> {t('settings.copied')}</>) : (<><I.copy size={12} /> {t('settings.copy')}</>)}
                  </button>
                </div>
              </div>
            </div>
            <div className="connector-method connector-chatgpt">
              <div className="connector-method-info">
                <strong>{t('settings.connectorChatgptTitle')}</strong>
                <span className="muted">{t('settings.connectorChatgptDesc')}</span>
              </div>
              <div className="connector-method-action connector-method-action-code">
                <div className="connector-chatgpt-options">
                  {/* Approved into the ChatGPT app directory 2026-08-19, so the
                      primary route is now one click on the listing. The old
                      Developer-mode paste survives as the "manual" option below
                      for Codex/IDE and anyone who can't reach the directory. */}
                  <div className="connector-chatgpt-option">
                    <strong>{t('settings.connectorChatgptAppTitle')}</strong>
                    <span className="muted">{t('settings.connectorChatgptInstallDesc', { defaultValue: 'Dathent is in the ChatGPT app directory — open it, press Connect, and sign in with your Dathent account.' })}</span>
                    <div className="connector-url-row">
                      <a className="btn btn-sm btn-violet" href={CHATGPT_APP_URL} target="_blank" rel="noopener noreferrer">
                        <I.external size={12} /> {t('settings.connectorChatgptInstall', { defaultValue: 'Open in ChatGPT' })}
                      </a>
                    </div>
                  </div>
                  <div className="connector-chatgpt-option">
                    <strong>{t('settings.connectorCodexTitle')}</strong>
                    <div className="connector-url-row">
                      <code className="connector-url-code" style={{ fontFamily: CONNECTOR_MONO_FONT }}>{CODEX_ADD_COMMAND}</code>
                      <button type="button" className={`btn btn-sm ${codexAddCopied ? 'btn-violet' : 'btn-ghost'}`} onClick={copyCodexAddCommand}>
                        {codexAddCopied ? (<><I.check size={12} /> {t('settings.copied')}</>) : (<><I.copy size={12} /> {t('settings.copy')}</>)}
                      </button>
                    </div>
                    <div className="connector-url-row">
                      <code className="connector-url-code" style={{ fontFamily: CONNECTOR_MONO_FONT }}>{CODEX_LOGIN_COMMAND}</code>
                      <button type="button" className={`btn btn-sm ${codexLoginCopied ? 'btn-violet' : 'btn-ghost'}`} onClick={copyCodexLoginCommand}>
                        {codexLoginCopied ? (<><I.check size={12} /> {t('settings.copied')}</>) : (<><I.copy size={12} /> {t('settings.copy')}</>)}
                      </button>
                    </div>
                  </div>
                  <div className="connector-chatgpt-option">
                    <strong>{t('settings.connectorChatgptManualTitle', { defaultValue: 'Add it manually instead' })}</strong>
                    <span className="muted">{t('settings.connectorChatgptSteps')}</span>
                    <div className="connector-url-row">
                      <code className="connector-url-code" style={{ fontFamily: CONNECTOR_MONO_FONT }}>{MCP_ENDPOINT_URL}</code>
                      <button type="button" className={`btn btn-sm ${urlCopied ? 'btn-violet' : 'btn-ghost'}`} onClick={copyEndpoint}>
                        {urlCopied ? (<><I.check size={12} /> {t('settings.copied')}</>) : (<><I.copy size={12} /> {t('settings.copy')}</>)}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <a
            className="connector-learn-more"
            href="https://dathent.ai/connect"
            target="_blank"
            rel="noopener"
          >
            {t('settings.connectorStepLearnMore', 'Learn more about connecting AI assistants to Dathent')}
          </a>
        </div>
      </section>

      {/* OAuth grants */}
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.connectorGrantsTitle')}</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14, maxWidth: 640 }}>
            {t('settings.connectorGrantsDesc')}
          </p>
          {grantsLoading ? (
            <div className="muted">{t('settings.loading')}</div>
          ) : grantsError ? (
            <p className="muted" role="alert">{t('settings.connectorGrantsLoadFailed')}</p>
          ) : grants.length === 0 ? (
            <p className="muted">{t('settings.connectorNoGrants')}</p>
          ) : (
            <ul className="account-list">
              {grants.map((grant) => {
                const connected = fmtDateShort(grant.createdAt);
                const expires = fmtDateShort(grant.expiresAt);
                return (
                  <li key={grant.id} className="account-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div className="account-name">{grant.clientName || grant.clientId}</div>
                      <div className="account-sub" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        {connected && <span>{t('settings.connectorGrantConnected', { date: connected })}</span>}
                        {connected && expires && <span>·</span>}
                        {expires && <span>{t('settings.connectorExpires', { date: expires })}</span>}
                      </div>
                      <div className="connector-scope-chips">
                        {(grant.scopes || []).map((scope) => (
                          <span key={scope} className="pill">{t(`settings.${SCOPE_LABEL_KEYS[scope] || ''}`, scope)}</span>
                        ))}
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setGrantRevokeTarget(grant)}
                      type="button"
                    >
                      {t('settings.connectorRevokeButton')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Existing tokens */}
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.connectorTokensTitle', 'API tokens')}</h3></div>
        <div className="s-card-body">
          {loading ? (
            <div className="muted">{t('settings.loading')}</div>
          ) : keys.length === 0 ? (
            <p className="muted">{t('settings.connectorNoTokens', 'No tokens yet — create one for developer tools such as Claude Code or mcp-remote. Claude and ChatGPT connect with OAuth without a token.')}</p>
          ) : (
            <ul className="account-list">
              {keys.map((k) => {
                const st = statusPillProps(k.status);
                const created = fmtDateShort(k.createdAt);
                const lastUsed = fmtDateShort(k.lastUsedAt);
                const expires = fmtDateShort(k.expiresAt);
                return (
                  <li key={k.id} className="account-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div className="account-name">{k.name}</div>
                      <div className="account-sub" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        <code style={{ fontFamily: CONNECTOR_MONO_FONT }}>{k.keyPrefix}···</code>
                        <span>·</span>
                        <span>{created ? t('settings.connectorCreated', { date: created, defaultValue: 'Created {{date}}' }) : null}</span>
                        <span>·</span>
                        <span>
                          {lastUsed
                            ? t('settings.connectorLastUsed', { date: lastUsed, defaultValue: 'Last used {{date}}' })
                            : t('settings.connectorNeverUsed', 'Never used')}
                        </span>
                        {expires && (
                          <>
                            <span>·</span>
                            <span>{t('settings.connectorExpires', { date: expires, defaultValue: 'Expires {{date}}' })}</span>
                          </>
                        )}
                      </div>
                      <div className="connector-scope-chips">
                        {(k.scopes || []).map((s) => (
                          <span key={s} className="pill">{t(`settings.${SCOPE_LABEL_KEYS[s] || ''}`, s)}</span>
                        ))}
                      </div>
                    </div>
                    <span className={st.cls}><span className="dot" />{st.label}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => openRevoke(k)}
                      disabled={k.status !== 'active'}
                      type="button"
                    >
                      {t('settings.connectorRevokeButton', 'Revoke')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Create a new token */}
      <section className="s-card">
        <div className="s-card-head"><h3>{t('settings.connectorCreateTitle', 'Create a new token')}</h3></div>
        <div className="s-card-body">
          {reveal ? (
            <div className="connector-reveal">
              <p style={{ fontSize: 13, marginTop: 0 }}>{t('settings.connectorRevealIntro', 'Token created — copy it now:')}</p>
              <div className="connector-url-row">
                <code className="connector-url-code" style={{ fontFamily: CONNECTOR_MONO_FONT }}>{reveal.apiKey}</code>
                <button type="button" className={`btn btn-sm ${tokenCopied ? 'btn-violet' : 'btn-ghost'}`} onClick={copyToken}>
                  {tokenCopied ? (<><I.check size={12} /> {t('settings.copied', 'Copied')}</>) : (<><I.copy size={12} /> {t('settings.copy', 'Copy')}</>)}
                </button>
              </div>
              <div
                style={{
                  marginTop: 12, padding: '10px 13px', borderRadius: 10,
                  background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)',
                  fontSize: 13, color: 'var(--text, inherit)', lineHeight: 1.45,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <I.alert size={14} style={{ flexShrink: 0 }} />
                <span>{t('settings.connectorSaveNowNote', "Save it now — you won't see it again.")}</span>
              </div>
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 14 }} onClick={closeReveal} type="button">
                {t('settings.connectorDone', 'Done')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 480 }}>
              <label className="form-row">
                <span className="form-l">{t('settings.connectorNameLabel', 'Name')}</span>
                <input
                  className="ep-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('settings.connectorNamePlaceholder', 'e.g. Claude Desktop')}
                  maxLength={80}
                />
              </label>
              <label className="form-row">
                <span className="form-l">{t('settings.connectorExpiryLabel', 'Expires')}</span>
                <select
                  className="ep-input"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                >
                  <option value="none">{t('settings.connectorExpiryNone', 'No expiry')}</option>
                  <option value="30d">{t('settings.connectorExpiry30d', '30 days')}</option>
                  <option value="90d">{t('settings.connectorExpiry90d', '90 days')}</option>
                  <option value="1y">{t('settings.connectorExpiry1y', '1 year')}</option>
                </select>
              </label>
              <div>
                <span className="form-l" style={{ display: 'block', marginBottom: 8 }}>
                  {t('settings.connectorScopesLabel', 'Permissions')}
                </span>
                <div className="connector-scope-list">
                  {MCP_SCOPES.map((scope) => (
                    <label key={scope} className="connector-scope-item" style={{ alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={scopes.has(scope)}
                        onChange={() => toggleScope(scope)}
                        style={{ marginTop: 2 }}
                      />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span>
                          {t(`settings.${SCOPE_LABEL_KEYS[scope]}`)}
                          {UNUSED_MCP_SCOPES.has(scope) && (
                            <span className="muted" style={{ fontWeight: 400 }}> {t('settings.connectorScopeUnused', '(not used by any tool yet)')}</span>
                          )}
                        </span>
                        <span className="muted" style={{ fontSize: 11.5, lineHeight: 1.35 }}>
                          {t(`settings.${SCOPE_DESC_KEYS[scope]}`)}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="connector-publish-note">
                  <I.info size={12} style={{ flexShrink: 0 }} />
                  <span>{t('settings.connectorPublishDisabledNote', 'Publishing via connector is platform-disabled for now.')}</span>
                </p>
              </div>
              {createError && <p style={{ fontSize: 12.5, color: '#e5484d', margin: 0 }}>{createError}</p>}
              <button
                type="submit"
                className="btn btn-violet btn-sm"
                disabled={creating || !name.trim()}
                style={{ alignSelf: 'flex-start' }}
              >
                {creating ? t('settings.connectorCreating', 'Creating…') : t('settings.connectorCreateButton', 'Create token')}
              </button>
            </form>
          )}
        </div>
      </section>

      <ConfirmDialog
        open={!!grantRevokeTarget}
        tone="danger"
        title={t('settings.connectorGrantRevokeTitle')}
        description={t('settings.connectorGrantRevokeDesc', { name: grantRevokeTarget?.clientName || grantRevokeTarget?.clientId })}
        confirmLabel={t('settings.connectorRevokeButton')}
        confirming={grantRevoking}
        onConfirm={confirmGrantRevoke}
        onCancel={closeGrantRevoke}
      />

      <ConfirmDialog
        open={!!revokeTarget}
        tone="danger"
        title={t('settings.connectorRevokeTitle', 'Revoke this token?')}
        description={t('settings.connectorRevokeDesc', 'Connectors using it will stop working immediately.')}
        confirmLabel={t('settings.connectorRevokeButton', 'Revoke')}
        confirming={revoking}
        onConfirm={confirmRevoke}
        onCancel={closeRevoke}
      />
    </>
  );
}

/* ---------------- Main screen ---------------- */
// Legacy tab names from the old app and OAuth-return redirects → new pack ids.
const TAB_ALIASES = {
  social: 'accounts',
  integrations: 'accounts',
  brand: 'workspace',
  general: 'profile',
};

export const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  // Resolve the requested tab (aliases + unknown fallback) into a known id.
  const resolved = (() => {
    const raw = urlTab || 'billing';
    const aliased = TAB_ALIASES[raw] || raw;
    return TABS.find((t) => t.id === aliased) ? aliased : 'billing';
  })();
  const [active, setActive] = useState(resolved);

  // Follow the URL: when the user navigates with ?tab=… from elsewhere
  // (e.g. sidebar account menu) while Settings is already mounted, keep
  // `active` in sync. Also normalize alias / invalid values in the URL
  // so deep-links stay stable.
  useEffect(() => {
    if (resolved !== active) setActive(resolved);
    if (urlTab && urlTab !== resolved) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', resolved);
      setSearchParams(next, { replace: true });
    }
  }, [urlTab, resolved]);

  const { user } = useAuth();
  const { releaseCheckoutChoice } = usePendingOAuth();
  const {
    entitlements,
    ensureDashboardData,
    invalidateResource,
    refreshEntitlements: refreshSharedEntitlements,
    captureEntitlementsScope,
    isEntitlementsScopeCurrent,
    accountId,
    resumeSubmitted,
    isResumeSubmitted,
    markResumeSubmitted,
    fetchedAt,
  } = useDashboardData() || {};
  // Owner decision 2026-09-07 (item 6): entitlements is null until the fetch
  // resolves — during that window BillingTab's derived isFreePlan fails open
  // ("Current plan: Free"), so every visit to Plan & billing flashed that
  // before the real plan (often paid) painted over it a moment later. planKnown gates
  // BillingTab's render the same way Dashboard.jsx gates its own plan-
  // dependent widgets.
  const planKnown = Boolean(fetchedAt?.entitlements && effectiveAccessState(entitlements));
  const [counts, setCounts] = useState(null);
  const [trialConfirm, setTrialConfirm] = useState(null); // { plan }
  const [activatingTrial, setActivatingTrial] = useState(false);
  const [trialActivationNotice, setTrialActivationNotice] = useState(null); // { kind, title, body, invoiceUrl? }
  const trialActivationLifecycle = useRef(null);

  useEffect(() => {
    const lifecycle = {};
    trialActivationLifecycle.current = lifecycle;
    setTrialConfirm(null);
    setActivatingTrial(false);
    setTrialActivationNotice(null);
    return () => {
      if (trialActivationLifecycle.current === lifecycle) trialActivationLifecycle.current = null;
    };
  }, [accountId]);

  const captureTrialActivationScope = () => ({
    lifecycle: trialActivationLifecycle.current,
    entitlements: captureEntitlementsScope(),
  });
  const isTrialActivationCurrent = (scope) => Boolean(scope.lifecycle
    && scope.lifecycle === trialActivationLifecycle.current
    && isEntitlementsScopeCurrent(scope.entitlements));

  // Warm the remaining shared resources on direct Settings navigation.
  // The provider initializes entitlements on every app route; this call reuses
  // its in-flight requests and each resource's TTL.
  useEffect(() => {
    if (typeof ensureDashboardData === 'function') {
      ensureDashboardData().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when user lands on Billing or Usage so a fresh entitlements response
  // is in flight (cache TTL is 2 min — within that, this is a no-op).
  useEffect(() => {
    if ((active === 'billing' || active === 'usage') && typeof ensureDashboardData === 'function') {
      ensureDashboardData().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    let cancelled = false;
    api.content.feed({ limit: 1 })
      .then((res) => { if (!cancelled) setCounts(res?.counts || res?.data?.counts || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const tab of TABS) {
      if (!groups.has(tab.group)) groups.set(tab.group, []);
      groups.get(tab.group).push(tab);
    }
    return groups;
  }, []);

  const switchTab = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const openActivateTrialConfirm = (plan) => {
    if (activatingTrial) return;
    setTrialActivationNotice(null);
    setTrialConfirm({ plan });
  };

  const closeActivateTrialConfirm = () => {
    if (!activatingTrial) setTrialConfirm(null);
  };

  const refreshEntitlements = async (options = {}) => {
    if (typeof refreshSharedEntitlements === 'function') {
      return refreshSharedEntitlements(options);
    }
    if (typeof invalidateResource === 'function') {
      const next = await invalidateResource('entitlements', { fresh: true });
      return { entitlements: next, converged: options.until ? options.until(next) : true, cancelled: false };
    } else if (typeof ensureDashboardData === 'function') {
      await ensureDashboardData({ force: true });
    }
  };

  const retryActivationRefresh = async () => {
    if (activatingTrial) return;
    const scope = captureTrialActivationScope();
    setActivatingTrial(true);
    try {
      const refreshed = await refreshEntitlements({ until: trialActivationHasConverged, scope: scope.entitlements });
      if (!isTrialActivationCurrent(scope) || refreshed?.cancelled) return;
      setTrialActivationNotice(refreshed?.converged ? {
        kind: 'success',
        title: t('settings.activateTrialSuccess'),
      } : {
        kind: 'error',
        title: t('settings.billingStatusPending'),
        retryRefresh: true,
      });
    } catch (_) {
      if (isTrialActivationCurrent(scope)) setTrialActivationNotice({
        kind: 'error', title: t('settings.billingStatusPending'), retryRefresh: true,
      });
    } finally {
      if (isTrialActivationCurrent(scope)) setActivatingTrial(false);
    }
  };

  const handleActivateTrialNow = async () => {
    if (!trialConfirm || activatingTrial) return;
    const scope = captureTrialActivationScope();
    setActivatingTrial(true);
    try {
      const response = await api.billing.activateTrialNow();
      if (!isTrialActivationCurrent(scope)) return;
      const result = response?.data || response;

      if (result?.status === 'activated') {
        const refreshed = await refreshEntitlements({
          until: trialActivationHasConverged,
          scope: scope.entitlements,
        });
        if (!isTrialActivationCurrent(scope) || refreshed?.cancelled) return;
        setTrialActivationNotice(refreshed.converged ? {
          kind: 'success',
          title: t('settings.activateTrialSuccess'),
        } : {
          kind: 'error',
          title: t('settings.billingStatusPending'),
          retryRefresh: true,
        });
      } else if (result?.status === 'payment_pending') {
        setTrialActivationNotice({
          kind: 'error',
          title: t('settings.activateTrialPaymentPendingTitle', { defaultValue: 'Payment required' }),
          body: t('settings.activateTrialPaymentPendingBody', { defaultValue: 'Your trial has ended. Pay the outstanding invoice to activate your plan.' }),
          invoiceUrl: result.invoiceUrl || null,
        });
        await refreshEntitlements({ scope: scope.entitlements });
        if (!isTrialActivationCurrent(scope)) return;
      } else if (result?.status === 'needs_card') {
        setTrialActivationNotice({
          kind: 'error',
          title: t('settings.activateTrialNeedsCardTitle'),
          body: t('settings.activateTrialNeedsCardBody'),
          billingUrl: '/app/settings?tab=billing',
        });
      } else if (result?.status === 'not_trialing') {
        await refreshEntitlements({ scope: scope.entitlements });
        if (!isTrialActivationCurrent(scope)) return;
      } else {
        throw new Error('Unexpected trial activation response');
      }
    } catch (err) {
      if (!isTrialActivationCurrent(scope)) return;
      console.error('Billing action failed', { type: 'activate-trial-now', err });
      setTrialActivationNotice({
        kind: 'error',
        title: t('settings.activateTrialErrorTitle'),
        body: t('settings.activateTrialErrorBody'),
      });
    } finally {
      if (isTrialActivationCurrent(scope)) {
        setActivatingTrial(false);
        setTrialConfirm(null);
      }
    }
  };

  // Post-payment welcome: Stripe returns to ?tab=billing&checkout=success&… .
  // That param is otherwise inert (the entitlements sync is server-side via the
  // webhook), so showing a one-time welcome overlay here touches NOTHING in the
  // billing path — BillingTab still mounts behind it and runs its re-fetch.
  // Capture the flag on first render, then strip checkout/session_id (preserving
  // tab=billing, mirroring the AccountsTab cleanup) so it never re-shows on reload.
  const [showWelcome, setShowWelcome] = useState(() => searchParams.get('checkout') === 'success');
  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    releaseCheckoutChoice();
  }, [releaseCheckoutChoice]);
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success' && !searchParams.get('session_id')) return;
    try { trackOnce('card_added', { source: getFunnelSource() }); } catch { /* fail-silent */ }
    // Google Ads "Subscribe": value/currency from the newest paid invoice,
    // transaction_id = Stripe checkout session (mandatory — recurring payments
    // would otherwise dedupe into one conversion). Best-effort, never blocks.
    const txId = searchParams.get('session_id') || `checkout-${Date.now()}`;
    api.billing.getInvoices()
      .then((res) => pickInvoiceValue(res?.invoices || res?.data?.invoices || []))
      .catch(() => null)
      .then((v) => { try { trackSubscribeOnce(txId, v ? { value: v.value, currency: v.currency } : {}); } catch { /* fail-silent */ } });
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    next.delete('session_id');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { t } = useTranslation();
  // GH-939: warning dot on the accounts tab while a TikTok account lacks statistics.
  const tiktokStatisticsOff = useTikTokStatisticsAccounts().length > 0;

  return (
    <div className="settings-page">
      {showWelcome && <PostPayWelcome onClose={dismissWelcome} />}
      {/* Desktop sidebar — visible ≥901px via CSS. */}
      <aside className="sb">
        <h1>{t('settings.settingsTitle')}</h1>
        {Array.from(grouped.entries()).map(([group, tabs]) => (
          <React.Fragment key={group}>
            <div className="sb-group">{GROUP_LABEL_KEYS[group] ? t(`settings.${GROUP_LABEL_KEYS[group]}`) : group}</div>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`sb-item ${active === tab.id ? 'is-active' : ''}`}
                onClick={() => switchTab(tab.id)}
                type="button"
              >
                {TAB_LABEL_KEYS[tab.id] ? t(`settings.${TAB_LABEL_KEYS[tab.id]}`) : tab.label}
                {tab.id === 'accounts' && tiktokStatisticsOff && <span className="dot dot-warn" role="img" aria-label={t('socialMetrics.accountMetricsOff')} />}
              </button>
            ))}
          </React.Fragment>
        ))}
      </aside>

      {/* Mobile dropdown — visible ≤900px via CSS. Uses a native
          <select> with optgroups so iOS/Android render the system
          picker; group headers preserved (Account / Workspace /
          Billing) so tabs stay organized at smaller sizes where the
          horizontal scroll strip was hard to discover. */}
      <div className="s-tab-mobile">
        <label className="s-tab-mobile-l">{t('settings.settingsSection')}</label>
        <select
          className="s-tab-mobile-select"
          value={active}
          onChange={(e) => switchTab(e.target.value)}
        >
          {Array.from(grouped.entries()).map(([group, tabs]) => (
            <optgroup key={group} label={GROUP_LABEL_KEYS[group] ? t(`settings.${GROUP_LABEL_KEYS[group]}`) : group}>
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>{TAB_LABEL_KEYS[tab.id] ? t(`settings.${TAB_LABEL_KEYS[tab.id]}`) : tab.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <main className="settings-main">
        {trialActivationNotice && (
          <div
            className={`s-notice s-notice-${trialActivationNotice.kind}`}
            role={trialActivationNotice.kind === 'error' ? 'alert' : 'status'}
            style={{
              padding: '12px 14px',
              marginBottom: 16,
              borderRadius: 'var(--r-md)',
              border: `1px solid ${trialActivationNotice.kind === 'error' ? 'var(--danger)' : 'var(--success)'}`,
              background: trialActivationNotice.kind === 'error' ? 'var(--danger-soft)' : 'var(--success-soft)',
              color: trialActivationNotice.kind === 'error' ? 'var(--danger-text, var(--danger))' : 'var(--success-text, var(--success))',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
            }}
          >
            {trialActivationNotice.kind === 'error'
              ? <I.alert size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              : <I.check size={16} style={{ flexShrink: 0, marginTop: 2 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{trialActivationNotice.title}</div>
              {trialActivationNotice.body && (
                <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{trialActivationNotice.body}</div>
              )}
              {trialActivationNotice.invoiceUrl && (
                <a
                  className="btn btn-violet btn-sm"
                  href={trialActivationNotice.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ marginTop: 10, display: 'inline-flex' }}
                >
                  {t('settings.activateTrialPayInvoice')}
                </a>
              )}
              {trialActivationNotice.billingUrl && (
                <Link
                  className="btn btn-violet btn-sm"
                  to={trialActivationNotice.billingUrl}
                  style={{ marginTop: 10, display: 'inline-flex' }}
                >
                  {t('settings.activateTrialNeedsCardCta')}
                </Link>
              )}
              {trialActivationNotice.retryRefresh && (
                <button
                  className="btn btn-violet btn-sm"
                  type="button"
                  onClick={retryActivationRefresh}
                  disabled={activatingTrial}
                  style={{ marginTop: 10, display: 'inline-flex' }}
                >
                  {t('settings.refreshBillingStatus')}
                </button>
              )}
            </div>
            <button
              className="btn btn-ghost btn-sm btn-icon"
              type="button"
              onClick={() => setTrialActivationNotice(null)}
              aria-label={t('settings.dismiss')}
              style={{ flexShrink: 0 }}
            >
              <I.x size={13} />
            </button>
          </div>
        )}
        {active === 'profile' && <ProfileTab user={user} />}
        {active === 'security' && <SecurityTab />}
        {active === 'workspace' && <WorkspaceTab />}
        {active === 'accounts' && <AccountsTab />}
        {active === 'billing' && (
          <BillingTab
            entitlements={entitlements}
            planKnown={planKnown}
            onActivateTrialNow={openActivateTrialConfirm}
            activatingTrial={activatingTrial}
            onRefreshEntitlements={refreshEntitlements}
            captureEntitlementsScope={captureEntitlementsScope}
            isEntitlementsScopeCurrent={isEntitlementsScopeCurrent}
            accountId={accountId}
            resumeSubmitted={resumeSubmitted}
            isResumeSubmitted={isResumeSubmitted}
            markResumeSubmitted={markResumeSubmitted}
          />
        )}
        {active === 'usage' && (
          <UsageTab
            entitlements={entitlements}
            counts={counts}
            onActivateTrialNow={openActivateTrialConfirm}
            activatingTrial={activatingTrial}
          />
        )}
        {active === 'connectors' && <ConnectorsTab />}
      </main>
      <ConfirmDialog
        open={!!trialConfirm}
        tone="info"
        title={t('settings.activateTrialTitle')}
        description={t('settings.activateTrialDescription', { plan: trialConfirm?.plan })}
        confirmLabel={t('settings.activateTrialConfirm')}
        confirmingLabel={t('settings.activatingTrialNow')}
        cancelLabel={t('settings.activateTrialCancel')}
        confirming={activatingTrial}
        onConfirm={handleActivateTrialNow}
        onCancel={closeActivateTrialConfirm}
      />
    </div>
  );
};

export default Settings;
